// ../../jam-core.ts
import { is as is12, set as set6, on as on8, update as update7, until as until2 } from "minimal-state";

// ../../lib/state-tree.js
import { is, on, emit, clear, set } from "minimal-state";

// ../../lib/causal-log.js
var timeout = null;
function log() {
  clearTimeout(timeout);
  timeout = setTimeout(() => {
    console.log("\n----------------\n\n");
  }, 20);
  console.log(...arguments);
}

// ../../lib/state-tree.js
var root = {
  children: [],
  level: -1
};
var current = root;
var renderRoot = null;
var nUses = 0;
var renderTime = 0;
var updateSet = new Set();
function declare(Component, props, stableProps) {
  let key = props == null ? void 0 : props.key;
  let element = current.children.find(c => c.component === Component && c.key === key);
  let [newElement] = _run(Component, props, {
    element,
    stableProps
  });
  return newElement.fragment;
}
function use(Component, props, stableProps) {
  let isComponent = typeof Component === "function";
  if (!isComponent) {
    return useExternalState(Component, props);
  }
  let key = props == null ? void 0 : props.key;
  let element = current.children.find(c => c.component === Component && c.key === key);
  let [newElement] = _run(Component, props, {
    element,
    isUsed: true,
    stableProps
  });
  return newElement.fragment[0];
}
function event(Component, props, stableProps) {
  let key = props == null ? void 0 : props.key;
  let element = current.children.find(c => c.component === Component && c.key === key);
  let [newElement] = _run(Component, props, {
    element,
    isEvent: true,
    isUsed: true,
    stableProps
  });
  return newElement.fragment[0];
}
function _run(Component, props, _temp) {
  if (props === void 0) {
    props = null;
  }
  let {
    element,
    stableProps = null,
    isUsed = false,
    isEvent = false,
    isMount = false,
    isRoot = false,
    state
  } = _temp === void 0 ? {} : _temp;
  let mustUpdate = updateSet.has(element);
  if (mustUpdate) updateSet.delete(element);
  if (sameProps(element == null ? void 0 : element.props, props) && !mustUpdate) {
    element.renderTime = renderTime;
    if (element.isEvent) {
      resultToFragment(void 0, element.fragment);
    }
    return [element, void 0];
  }
  if (element === void 0) {
    isMount = true;
    element = {
      component: Component,
      render: Component,
      props,
      stableProps,
      key: props == null ? void 0 : props.key,
      children: [],
      uses: [],
      renderTime,
      parent: current,
      level: current.level + 1,
      root: renderRoot,
      isUsed,
      isEvent,
      fragment: Fragment()
    };
    if (isRoot) {
      element.actionSubs = new Map();
      element.stateSubs = new Map();
      element.state = state;
      element.root = element;
      renderRoot = element;
    }
    log2("mounting new element", element.component.name);
    current.children.push(element);
  } else {
    if (props !== null) element.props = props;
    if (stableProps !== null) element.stableProps = stableProps;
    element.renderTime = renderTime;
  }
  let renderProps = {
    ...element.props,
    ...element.stableProps
  };
  let tmp = [current, nUses];
  current = element;
  nUses = 0;
  let result;
  if (isMount) {
    result = Component(renderProps);
    if (typeof result === "function") {
      let mountUses = element.uses;
      element.uses = [];
      nUses = 0;
      element.render = result;
      result = element.render(renderProps);
      element.uses = element.uses.concat(mountUses);
    }
  } else {
    result = element.render(renderProps);
  }
  [current, nUses] = tmp;
  let children = element.children;
  for (let i = children.length - 1; i >= 0; i--) {
    let child = children[i];
    if (child.renderTime !== renderTime) {
      cleanup(child);
      children.splice(i, 1);
    }
  }
  log2("rendered", Component.name, "with result", result);
  let updateKeys = resultToFragment(result, element.fragment);
  return [element, updateKeys];
}
function cleanup(element) {
  var _a4;
  log2("unmounting element", element.component.name);
  clear(element.fragment);
  for (let use2 of element.uses) {
    (_a4 = use2 == null ? void 0 : use2.cleanup) == null ? void 0 : _a4.call(use2);
  }
  if (renderRoot !== null) {
    unsubscribeAll(renderRoot.actionSubs, element);
    unsubscribeAll(renderRoot.stateSubs, element);
  }
  for (let child of element.children) {
    cleanup(child);
  }
}
function declareStateRoot(Component, props, _ref) {
  if (props === void 0) {
    props = null;
  }
  let {
    state,
    defaultState: defaultState2 = {}
  } = _ref;
  current = root;
  state = state != null ? state : {
    ...defaultState2
  };
  renderTime = Date.now();
  const [rootElement] = _run(Component, props, {
    isMount: true,
    isRoot: true,
    state
  });
  const rootFragment = rootElement.fragment;
  let result = rootFragment[0];
  log2("initial root render returned", result, "after", Date.now() - renderTime);
  is(state, result);
  renderRoot = null;
  on(rootFragment, (result2, keys) => {
    log2("root fragment update triggered!", keys);
    if (keys === void 0) is(state, result2);else {
      for (let key of keys) {
        if (state[key] === result2[key]) {
          console.error("value did not change", key);
        }
        is(state, key, result2[key]);
      }
    }
  });
  on(state, (key, value, oldValue) => {
    if (oldValue !== void 0 && value === oldValue) return;
    let subscribers = rootElement.stateSubs.get(key);
    if (subscribers !== void 0) {
      for (let element of subscribers) {
        queueUpdate(element, "state " + key);
      }
    }
  });
  const dispatch = (type, payload) => dispatchFromRoot(rootElement, type, payload);
  on(state, "dispatch", dispatch);
  return {
    state,
    dispatch,
    setProps: function () {
      for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
        args[_key] = arguments[_key];
      }
      return setProps(rootElement, ...args);
    }
  };
}
function sameProps(prevProps, props) {
  if (prevProps === void 0) return false;
  if (prevProps === props) return true;
  if (prevProps === null || props === null || props === void 0) return false;
  for (let key in prevProps) {
    if (!(key in props) || props[key] !== prevProps[key]) return false;
  }
  for (let key in props) {
    if (!(key in prevProps) || props[key] !== prevProps[key]) return false;
  }
  return true;
}
function queueUpdate(caller, reason) {
  if (reason === void 0) {
    reason = "";
  }
  log2("queuing update", caller.component.name, reason);
  markForUpdate(caller);
  return Promise.resolve().then(() => {
    updateAll(caller.component.name + " " + reason);
  });
}
function updateAll(reason) {
  if (updateSet.size === 0) {
    return;
  }
  log2("tree update caused by", reason);
  let orderedElements = [...updateSet].sort((el1, el2) => el1.level - el2.level);
  for (let element of orderedElements) {
    if (!updateSet.has(element)) {
      continue;
    }
    renderRoot = element.root;
    renderTime = Date.now();
    log2("rerendering", element.component.name);
    let [, updateKeys] = _run(element.component, element.props, {
      element
    });
    log2("render took", Date.now() - renderTime);
    let result = element.fragment[0];
    emit(element.fragment, result, updateKeys);
    renderRoot = null;
  }
}
function markForUpdate(element) {
  let parent = element;
  updateSet.add(parent);
  if (!parent.isUsed) return parent;
  while (parent.parent !== root) {
    parent = parent.parent;
    updateSet.add(parent);
    if (!parent.isUsed) return parent;
  }
  return parent;
}
async function setProps(element) {
  for (var _len2 = arguments.length, args = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
    args[_key2 - 1] = arguments[_key2];
  }
  log2("setProps", ...args);
  let newProps = {
    ...element.props
  };
  set(newProps, ...args);
  if (sameProps(element.props, newProps)) return;
  element.props = newProps;
  return queueUpdate(element, "setProps");
}
async function dispatchFromRoot(rootElement, type, payload) {
  log2("dispatching", rootElement.component.name, String(type));
  let subscribers = rootElement.actionSubs.get(type);
  if (subscribers === void 0 || subscribers.size === 0) return;
  let promises = [];
  for (let element of subscribers) {
    for (let use2 of element.uses) {
      if (use2.action === type) {
        use2.push(payload);
        let promise = queueUpdate(element, "dispatch " + type);
        promises.push(promise);
      }
    }
  }
  await Promise.all(promises);
}
function subscribe(subscriptions, type, element) {
  var _a4;
  let subscribers = (_a4 = subscriptions.get(type)) != null ? _a4 : subscriptions.set(type, new Set()).get(type);
  subscribers.add(element);
}
function unsubscribeAll(subscriptions, element) {
  for (let entry of subscriptions) {
    let [key, set7] = entry;
    set7.delete(element);
    if (set7.size === 0) {
      subscriptions.delete(key);
    }
  }
}
function Action(type) {
  return {
    type,
    toString: () => type
  };
}
function useAction(type) {
  if (current === root) throw Error("Hooks can only be called during render");
  let actionQueue = current.uses[nUses];
  if (actionQueue === void 0) {
    actionQueue = [];
    actionQueue.action = type;
    subscribe(renderRoot.actionSubs, type, current);
    current.uses[nUses] = actionQueue;
  }
  nUses++;
  if (actionQueue.length > 0) {
    let payload = actionQueue.shift();
    if (actionQueue.length > 0) {
      queueUpdate(current, "queued dispatch " + type);
    }
    return [true, payload];
  } else {
    return [false, void 0];
  }
}
function useDispatch() {
  let callerRoot = renderRoot;
  return (type, payload) => dispatchFromRoot(callerRoot, type, payload);
}
function useRootState(keys) {
  if (current === root) throw Error("Hooks can only be called during render");
  let {
    state
  } = renderRoot;
  if (Array.isArray(keys)) {
    let values = [];
    for (let key of keys) {
      subscribe(renderRoot.stateSubs, key, current);
      values.push(state[key]);
    }
    return values;
  } else if (keys !== void 0) {
    subscribe(renderRoot.stateSubs, keys, current);
    return state[keys];
  } else {
    return state;
  }
}
function useExternalState(state, keyOrSelector) {
  if (current === root) throw Error("Hooks can only be called during render");
  let caller = current;
  let use2 = caller.uses[nUses];
  let [key, selector] = typeof keyOrSelector === "function" ? [void 0, keyOrSelector] : [keyOrSelector, void 0];
  if (use2 === void 0 || use2.key !== keyOrSelector) {
    if (use2 !== void 0) {
      use2.cleanup();
    } else {
      use2 = {
        key: keyOrSelector
      };
    }
    if (selector !== void 0) {
      try {
        use2.value = selector(state);
      } catch (err) {
        console.warn(err);
      }
    }
    let listener = () => {
      if (current === caller) return;
      if (selector !== void 0) {
        let value = selector(state);
        if (use2.value === value) return;
        use2.value = value;
      }
      queueUpdate(caller, "useExternalState " + key);
    };
    use2.cleanup = key === void 0 ? on(state, listener) : on(state, key, listener);
    caller.uses[nUses] = use2;
  }
  nUses++;
  return key === void 0 ? use2.value : state[key];
}
function useEvent(emitter, keyOrSelector) {
  if (current === root) throw Error("Hooks can only be called during render");
  let caller = current;
  let eventQueue = caller.uses[nUses];
  if (eventQueue === void 0 || eventQueue.key !== keyOrSelector) {
    if (eventQueue === void 0) {
      eventQueue = [];
    } else {
      eventQueue.cleanup();
    }
    eventQueue.key = keyOrSelector;
    let [key, selector] = typeof keyOrSelector === "function" ? [void 0, keyOrSelector] : [keyOrSelector, void 0];
    let listener = function () {
      for (var _len3 = arguments.length, args = new Array(_len3), _key3 = 0; _key3 < _len3; _key3++) {
        args[_key3] = arguments[_key3];
      }
      if (selector !== void 0 && !selector(...args)) return;
      eventQueue.push(args);
      queueUpdate(caller, "useEvent " + key);
    };
    eventQueue.cleanup = key === void 0 ? on(emitter, listener) : on(emitter, key, listener);
    current.uses[nUses] = eventQueue;
  }
  nUses++;
  if (eventQueue.length > 0) {
    let args = eventQueue.shift();
    if (eventQueue.length > 0) {
      queueUpdate(current, "queued useEvent ");
    }
    return [true, ...args];
  } else {
    return [false];
  }
}
function useOn() {
  if (current === root) throw Error("Hooks can only be called during render");
  let caller = current;
  let use2 = caller.uses[nUses];
  if (use2 !== void 0) {
    use2.cleanup();
  }
  let cleanup2 = on(...arguments);
  caller.uses[nUses] = {
    cleanup: cleanup2
  };
  nUses++;
}
function useUpdate() {
  if (current === root) throw Error("Hooks can only be called during render");
  let caller = current;
  return () => queueUpdate(caller, "useUpdate");
}
function useState(initial) {
  if (current === root) throw Error("Hooks can only be called during render");
  let caller = current;
  let use2 = caller.uses[nUses];
  if (use2 === void 0) {
    const setState = value => {
      if (value === use2[0]) return;
      use2[0] = value;
      if (current !== caller) {
        queueUpdate(caller, "useState " + value);
      }
      return value;
    };
    use2 = [initial, setState];
    caller.uses[nUses] = use2;
  }
  nUses++;
  return use2;
}
function useUnmount(cleanup2) {
  if (current === root) throw Error("Hooks can only be called during render");
  current.uses[nUses] = {
    cleanup: cleanup2
  };
  nUses++;
}
function Fragment() {
  let fragment = [void 0];
  fragment._frag = true;
  fragment._deps = new Map();
  fragment._type = "nullish";
  on(fragment, function () {
    for (let e of fragment._deps) {
      e[1](...arguments);
    }
  });
  return fragment;
}
function isFragment(thing) {
  return thing == null ? void 0 : thing._frag;
}
function resultToFragment(result, fragment) {
  if (result === void 0 || result === null) {
    fragment._type = "nullish";
    fragment[0] = result;
    return;
  }
  if (result._frag) {
    fragment[0] = result[0];
    fragment._type = result._type;
    result._deps.set(fragment, (value, keys) => {
      fragment[0] = value;
      emit(fragment, value, keys);
    });
    return;
  }
  if (result._atom) {
    fragment[0] = result[0];
    fragment._type = "plain";
    return;
  }
  if (result._merged) {
    let updateKeys = setMergedFragment(fragment, result);
    fragment._type = "merged";
    return updateKeys;
  }
  if (Array.isArray(result) || result instanceof Map || result instanceof Set) {
    fragment[0] = result;
    fragment._type = "plain";
    return;
  }
  if (typeof result === "object") {
    let updateKeys = setObjectFragment(fragment, result);
    fragment._type = "object";
    return updateKeys;
  }
  fragment[0] = result;
  fragment._type = "plain";
}
function setObjectFragment(fragment, obj) {
  let pureObj = {};
  let keys = fragment._type === "object" ? [] : void 0;
  let oldObj = fragment[0];
  fragment[0] = pureObj;
  for (let key in obj) {
    let prop = obj[key];
    if (isFragment(prop)) {
      pureObj[key] = prop[0];
      prop._deps.set(fragment, value => {
        if (pureObj[key] === value) return;
        pureObj[key] = value;
        emit(fragment, pureObj, [key]);
      });
    } else {
      pureObj[key] = prop;
    }
    if (keys !== void 0 && oldObj[key] !== pureObj[key]) {
      keys.push(key);
    }
  }
  return keys;
}
function merge() {
  for (var _len4 = arguments.length, objArray = new Array(_len4), _key4 = 0; _key4 < _len4; _key4++) {
    objArray[_key4] = arguments[_key4];
  }
  objArray._merged = true;
  return objArray;
}
function setMergedFragment(fragment, objArray) {
  let mergedObj = {};
  let oldObj = fragment[0];
  fragment[0] = mergedObj;
  function updateSelf(value, keys2) {
    let objects = objArray.map(oa => oa._frag ? oa[0] : oa);
    fragment[0] = Object.assign(mergedObj, ...objects);
    emit(fragment, fragment[0], keys2 != null ? keys2 : Object.keys(value));
  }
  for (let objFragment of objArray) {
    if (isFragment(objFragment)) {
      Object.assign(mergedObj, objFragment[0]);
      objFragment._deps.set(fragment, updateSelf);
    } else {
      Object.assign(mergedObj, objFragment);
    }
  }
  let keys;
  if (fragment._type === "merged") {
    keys = [];
    for (let key in mergedObj) {
      if (oldObj[key] !== mergedObj[key]) {
        keys.push(key);
      }
    }
  }
  return keys;
}
var _a;
var doLog = !!((_a = window.jamConfig) == null ? void 0 : _a.development);
function debugStateTree() {
  window.root = root;
  doLog = true;
}
function log2() {
  if (doLog === false) return;
  for (var _len5 = arguments.length, args = new Array(_len5), _key5 = 0; _key5 < _len5; _key5++) {
    args[_key5] = arguments[_key5];
  }
  log("STATE-TREE", ...args);
}

// ../../lib/state-utils.js
import { on as on2 } from "minimal-state";
function useDidChange(value, initial) {
  let [oldValue, setValue] = useState(initial);
  if (value !== oldValue) {
    setValue(value);
    return true;
  } else {
    return false;
  }
}
function useDidEverChange(value, initial) {
  let [hasChanged, setChanged] = useState(false);
  let [oldValue, setValue] = useState(initial);
  if (value !== oldValue) {
    setValue(value);
    setChanged(true);
    return [true, true];
  } else {
    return [false, hasChanged];
  }
}
function debug(state) {
  on2(state, (key, value, oldValue) => {
    if (oldValue !== void 0) {
      log(key, oldValue, "->", value);
    } else {
      log(key, value);
    }
  });
}

// ../../lib/identity-utils.js
import { toBytes as toBytes2, toBase64 as toBase642 } from "fast-base64/js";
import { toUrl, fromUrl } from "fast-base64/url";

// ../../lib/watsign-records.js
import { sign, verify } from "watsign";
import { toBytes, toBase64 } from "fast-base64";

// ../../lib/util.js
function mergeObject(obj, partialObj) {
  for (let key in partialObj) {
    let value = partialObj[key];
    if (value !== void 0) {
      obj[key] = value;
    }
  }
}
function domEvent(el, event2) {
  return new Promise(resolve => {
    el.addEventListener(event2, function onEvent() {
      el.removeEventListener(event2, onEvent);
      resolve();
    });
  });
}
function concatBytes() {
  for (var _len6 = arguments.length, arrays = new Array(_len6), _key6 = 0; _key6 < _len6; _key6++) {
    arrays[_key6] = arguments[_key6];
  }
  if (!arrays.length) return null;
  let totalLength = arrays.reduce((acc, value) => acc + value.length, 0);
  let result = new Uint8Array(totalLength);
  let length = 0;
  for (let array of arrays) {
    result.set(array, length);
    length += array.length;
  }
  return result;
}

// ../../lib/watsign-records.js
async function signData(_ref2) {
  let {
    record,
    keypair: keypair2,
    validSeconds,
    validUntil
  } = _ref2;
  let Certified = await toBase64(new TextEncoder().encode(JSON.stringify(record)));
  let Expiration = validUntil || (validSeconds || 1800) + Math.floor(Date.now() / 1e3);
  let bytesToSign = await createBytesToSign({
    Version: 0,
    Expiration,
    KeyType: "ed25519",
    Certified
  });
  let Signatures = [];
  try {
    Signatures.push(await createSignature(bytesToSign, keypair2));
  } catch {}
  return {
    Version: 0,
    Expiration,
    KeyType: "ed25519",
    Certified,
    Signatures
  };
}
async function verifyData(signedRecord) {
  try {
    let bytesToSign = createBytesToSign(signedRecord);
    if (signedRecord.Version !== 0) {
      return false;
    }
    return await Promise.all(signedRecord.Signatures.map(async signature => {
      return verify(bytesToSign, await toBytes(signature.Payload), await toBytes(signature.Identity));
    })).every(s3 => s3);
  } catch (e) {
    return false;
  }
}
async function getVerifiedData(signedRecord) {
  if (await verifyData(signedRecord)) {
    let dataRaw = await toBytes(signedRecord.Certified);
    let data = JSON.parse(new TextDecoder().decode(dataRaw));
    return {
      data,
      identities: signedRecord.Signatures.map(s3 => s3.Identity)
    };
  }
  return void 0;
}
async function createSignature(bytesToSign, _ref3) {
  let {
    publicKey,
    secretKey
  } = _ref3;
  let signatureBytes = await sign(bytesToSign, secretKey);
  let signature = await toBase64(signatureBytes);
  return {
    Identity: await toBase64(publicKey),
    Payload: signature
  };
}
async function createBytesToSign(_ref4) {
  let {
    Version,
    Expiration,
    KeyType,
    Certified
  } = _ref4;
  let versionBytes = new Uint8Array(4);
  let expirationBytes = new Uint8Array(8);
  let keyTypeBytes = new TextEncoder().encode(KeyType);
  let payloadBytes = await toBytes(Certified);
  for (let i = 0; i < 4; i++) {
    versionBytes[i] = Version >> 8 * i & 255;
  }
  for (let i = 0; i < 8; i++) {
    expirationBytes[i] = Expiration >> 8 * i & 255;
  }
  return concatBytes(versionBytes, expirationBytes, keyTypeBytes, payloadBytes);
}

// ../../lib/identity-utils.js
var MESSAGE_VALIDITY_SECONDS = 300;
async function signData2(identity, data) {
  return signData({
    record: data,
    keypair: keypair(identity),
    validSeconds: MESSAGE_VALIDITY_SECONDS
  });
}
async function verifyData2(record, key) {
  let verifiedRecord = await getVerifiedData(record);
  if (!verifiedRecord) return;
  let {
    identities: identities2,
    data
  } = verifiedRecord;
  if (!identities2.includes(fromUrl(key))) return;
  return data;
}
async function signedToken(identity) {
  let signed = await signData2(identity, {});
  return encode(new TextEncoder().encode(JSON.stringify(signed)));
}
function keypair(identity) {
  return {
    secretKey: decode(identity.secretKey),
    publicKey: decode(identity.publicKey)
  };
}
function decode(base64String) {
  return toBytes2(fromUrl(base64String));
}
function encode(binaryData) {
  return toUrl(toBase642(binaryData));
}

// ../../lib/avatar.js
var roomDisplayName = (info, room) => {
  var _a4, _b, _c, _d, _e, _f;
  if ((_a4 = room.userDisplay) == null ? void 0 : _a4.identities) {
    return room.userDisplay.identities[info.id].name || selectFromList(info.id, names);
  } else if ((_b = room.userDisplay) == null ? void 0 : _b.names) {
    return room.userDisplay.names[info.id] || selectFromList(info.id, names);
  } else if ((_c = room.userDisplay) == null ? void 0 : _c.randomIdentities) {
    return selectFromList(info.id, (_d = room.userDisplay) == null ? void 0 : _d.randomIdentities).name;
  } else if ((_e = room.userDisplay) == null ? void 0 : _e.randomNames) {
    return selectFromList(info.id, (_f = room.userDisplay) == null ? void 0 : _f.randomNames);
  } else {
    return selectFromList(info.id, names);
  }
};
var displayName = (info, room) => {
  var _a4;
  const infoName = info.name || info.displayName;
  if (infoName && !((_a4 = room.access) == null ? void 0 : _a4.lockedIdentities)) {
    return infoName;
  } else if (room) {
    return roomDisplayName(info, room);
  } else {
    return selectFromList(info.id, names);
  }
};
var selectFromList = (id, list) => {
  return list[publicKeyToIndex(id, list.length)];
};
var names = ["Ali", "Alex", "Ash", "Blue", "Chi", "Drew", "Eight", "Fin", "Floor", "Five", "Four", "Jam", "Jaz", "Misha", "Mu", "Nine", "One", "Pat", "Sam", "Sasha", "Seven", "Six", "Sky", "Sol", "Storm", "Sun", "Tao", "Ten", "Three", "Tsu", "Two", "Yu", "Zero"];
var integerFromBytes = rawBytes => rawBytes[0] + (rawBytes[1] << 8) + (rawBytes[2] << 16) + (rawBytes[3] << 24);
function publicKeyToIndex(publicKey, range) {
  const bytes = decode(publicKey);
  return Math.abs(integerFromBytes(bytes)) % range;
}

// ../../jam-core/identity.ts
import { keyPairFromSecretKey, keyPairFromSeed, newKeyPair } from "watsign";
import { set as set5 } from "minimal-state";

// ../../lib/local-storage.js
import { on as on3, pure, set as set2 } from "minimal-state";
function StoredState(name, initialize) {
  if (initialize === void 0) {
    initialize = () => ({});
  }
  let state = getStorage(localStorage, name);
  if (!state) {
    state = initialize();
    setStorage(localStorage, name, state);
  }
  let forwardUpdates = true;
  on3(state, () => {
    if (forwardUpdates) setStorage(localStorage, name, pure(state));
  });
  window.addEventListener("storage", event2 => {
    var _a4;
    if (event2.key === name) {
      forwardUpdates = false;
      try {
        let storedState = (_a4 = getStorage(localStorage, name)) != null ? _a4 : initialize();
        for (let key in storedState) {
          let value = storedState[key];
          if (state[key] !== value) set2(state, key, value);
        }
      } catch (_) {}
      forwardUpdates = true;
    }
  });
  return state;
}
function getStorage(storage, key) {
  let content = parse(storage.getItem(key));
  if (content === null) content = void 0;
  return content;
}
function setStorage(storage, key, value) {
  var _a4;
  storage.setItem(key, (_a4 = JSON.stringify(value)) != null ? _a4 : null);
}
function parse(json) {
  if (json === void 0) return;
  try {
    return JSON.parse(json);
  } catch (e) {}
}

// ../../lib/migrations.js
import { set as set3, update } from "minimal-state";
function migrateDisplayName(identities2) {
  for (let key in identities2) {
    let {
      info
    } = identities2[key];
    if (info.displayName !== void 0) {
      console.warn("migrating identity", key, identities2[key]);
      if (info.name === void 0) {
        info.name = info.displayName;
      }
      delete info.displayName;
      set3(identities2, key, identities2[key]);
    }
  }
}
function importLegacyIdentity() {
  const identity = getStorage(localStorage, "identity");
  if (!identity) return;
  if (!identity.publicKey && identity.keyPair.publicKey) {
    set3(identity, "publicKey", identity.keyPair.publicKey);
    set3(identity, "secretKey", identity.keyPair.secretKey);
  }
  if (!identity.info) {
    set3(identity, "info", {
      id: identity.publicKey
    });
  }
  if (!identity.info.id) {
    identity.info.id = identity.publicKey;
    update(identity, "info");
  }
  if (identity.info.twitter) {
    let twitterIdentity = {
      type: "twitter",
      id: identity.info.twitter,
      verificationInfo: identity.info.tweet
    };
    if (!identity.info.identities || !identity.info.identities.length || !identity.info.identities[0].id) {
      set3(identity, "info", {
        ...identity.info,
        identities: [twitterIdentity]
      });
    }
    set3(identity, "info", {
      ...identity.info,
      twitter: void 0,
      tweet: void 0
    });
  }
  return identity;
}

// ../../jam-core/backend.js
import { on as on4 } from "minimal-state";

// ../../jam-core/config.ts
var defaultConfig = {
  urls: {
    pantry: `https://jam.systems/_/pantry`,
    stun: `stun:stun.jam.systems:3478`,
    turn: `turn:turn.jam.systems:3478`,
    turnCredentials: {
      username: "test",
      credential: "yieChoi0PeoKo8ni"
    }
  },
  development: false,
  sfu: false,
  broadcast: false
};
var _a2;
var staticConfig = (_a2 = window.jamConfig) != null ? _a2 : defaultConfig;

// ../../lib/GetRequest.js
import { update as update2 } from "minimal-state";
function GetRequest(_ref5) {
  let {
    path,
    dontFetch,
    fetchOnMount,
    getToken
  } = _ref5;
  let ourState = "idle";
  let shouldFetch = !!path && !dontFetch;
  if (shouldFetch && fetchOnMount) {
    ourState = "loading";
    getRequest(path, getToken);
  }
  return function GetRequest2(_ref6) {
    let {
      path: path2,
      dontFetch: dontFetch2,
      getToken: getToken2
    } = _ref6;
    var _a4;
    let {
      state,
      data,
      status
    } = (_a4 = use(queryCache, path2)) != null ? _a4 : idleQuery;
    let shouldFetch2 = !!path2 && !dontFetch2;
    let actionRequired = shouldFetch2 && state === "idle";
    if (!shouldFetch2) {
      ourState = "idle";
      return {
        data: null,
        status: null,
        isLoading: false
      };
    }
    if (actionRequired) {
      ourState = "loading";
      getRequest(path2, getToken2);
    } else {
      ourState = state;
    }
    let isLoading = ourState === "loading";
    return {
      data,
      status,
      isLoading
    };
  };
}
async function getRequest(path, getToken) {
  var _a4;
  setCache(path, {
    state: "loading"
  });
  let headers = {
    Accept: "application/json"
  };
  if (getToken) headers.Authorization = `Token ${await getToken()}`;
  let res = await fetch(path, {
    headers
  }).catch(console.warn);
  let {
    state,
    data,
    status
  } = getCache(path);
  if (state !== "loading") {
    return [data, state === "success", status];
  }
  if (res === void 0) {
    setCache(path, idleQuery);
    return [null, false, null];
  }
  status = res.status;
  let ok = status < 400;
  if (ok) {
    state = "success";
    data = (_a4 = await res.json().catch(console.warn)) != null ? _a4 : null;
  } else {
    state = "error";
    data = null;
  }
  setCache(path, {
    state,
    data,
    status
  });
  return [data, ok, status];
}
var queryCache = {};
var idleQuery = {
  state: "idle",
  data: null,
  status: null
};
function getCache(path) {
  var _a4;
  return (_a4 = queryCache[path]) != null ? _a4 : idleQuery;
}
function setCache(path, _ref7) {
  let {
    state,
    data,
    status
  } = _ref7;
  let cached = queryCache[path];
  if (cached === void 0) {
    queryCache[path] = {
      state,
      data: data != null ? data : null,
      status: status != null ? status : null
    };
  } else {
    mergeObject(cached, {
      state,
      data,
      status
    });
  }
  update2(queryCache, path);
}
function populateCache(path, data) {
  setCache(path, {
    data,
    state: "success",
    status: 200
  });
}

// ../../lib/state-diff.js
function useStableArray(newValue) {
  let [value, set7] = useState(newValue);
  return arrayEqual(value, newValue) ? value : set7(newValue);
}
function useStableObject(newValue) {
  let [value, set7] = useState(newValue);
  return objectEqual(value, newValue) ? value : set7(newValue);
}
function arrayEqual(a, b) {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}
function objectEqual(a, b) {
  if (a === b) return true;
  let keys = Object.keys(a);
  let length = keys.length;
  for (let i = 0; i < length; i++) {
    if (a[keys[i]] !== b[keys[i]]) return false;
  }
  return length === Object.keys(b).length;
}

// ../../jam-core/room/Speakers.js
import { is as is2 } from "minimal-state";

// ../../jam-core/state.ts
var defaultProps = {
  roomId: null,
  userInteracted: false,
  micMuted: false,
  handRaised: false,
  autoJoin: false,
  hasMediasoup: false,
  hasBroadcast: false,
  customStream: null
};
var defaultState = {
  myIdentity: null,
  myId: null,
  roomId: null,
  inRoom: null,
  room: {
    name: "",
    description: "",
    speakers: [],
    moderators: [],
    presenters: []
  },
  hasRoom: false,
  isRoomLoading: false,
  iAmSpeaker: false,
  iAmModerator: false,
  iAmPresenter: true,
  identities: {},
  otherDeviceInRoom: false,
  swarm: null,
  peers: [],
  peerState: {},
  myPeerState: {
    inRoom: false,
    micMuted: false,
    leftStage: false,
    isRecording: false
  },
  reactions: {},
  handRaised: false,
  soundMuted: true,
  micMuted: false,
  audioFile: null,
  audioFileElement: null,
  myAudio: null,
  availableMicrophones: [],
  myVideo: null,
  audioPlayError: false,
  hasMicFailed: false,
  speaking: new Set(),
  isRecording: false,
  isSomeoneRecording: false,
  recordedAudio: null,
  isPodcasting: false,
  isSomeonePodcasting: false,
  podcastTracks: {}
};
var actions = {
  JOIN: Action("join"),
  LEAVE_STAGE: Action("leave-stage"),
  RETRY_CAM: Action("retry-cam"),
  SWITCH_CAM: Action("switch-cam"),
  SET_CAM_ON: Action("set-cam-on"),
  RETRY_MIC: Action("retry-mic"),
  SELECT_MIC: Action("select-mic"),
  RETRY_AUDIO: Action("retry-audio"),
  REACTION: Action("reaction"),
  AUTO_JOIN: Action("auto-join"),
  START_SERVER_RECORDING: Action("start-server-recording"),
  STOP_SERVER_RECORDING: Action("stop-server-recording"),
  START_RECORDING: Action("start-recording"),
  STOP_RECORDING: Action("stop-recording"),
  START_PODCAST_RECORDING: Action("start-podcast-recording"),
  STOP_PODCAST_RECORDING: Action("stop-podcast-recording"),
  START_SCREEN_SHARE: Action("start-screen-share"),
  STOP_SCREEN_SHARE: Action("stop-screen-share"),
  BACKCHANNEL_SUBSCRIBE: Action("backchannel-subscribe"),
  BACKCHANNEL_UNSUBSCRIBE: Action("backchannel-unsubscribe"),
  BACKCHANNEL_BROADCAST: Action("backchannel-broadcast")
};

// ../../jam-core/room/Speakers.js
function Speakers() {
  const leftStageRooms = StoredState("jam.leftStageRooms", () => ({}));
  const leftStageMap = new Map();
  return function Speakers2(_ref8) {
    let {
      roomId,
      hasRoom,
      room,
      peerState,
      myPeerState,
      myIdentity
    } = _ref8;
    var _a4;
    let leftStagePeers = (_a4 = leftStageMap.get(roomId)) != null ? _a4 : leftStageMap.set(roomId, new Set()).get(roomId);
    let {
      speakers,
      stageOnly
    } = room;
    let myId = myIdentity.publicKey;
    let [isLeaveStage] = useAction(actions.LEAVE_STAGE);
    let justGotRoom = useDidChange(hasRoom) && hasRoom;
    let iAmServerSpeaker = !!stageOnly || speakers.includes(myId);
    let iBecameSpeaker = useDidChange(iAmServerSpeaker) && iAmServerSpeaker && !justGotRoom;
    if (iBecameSpeaker) {
      is2(leftStageRooms, roomId, void 0);
      leftStagePeers.delete(myId);
    }
    if (isLeaveStage) {
      is2(leftStageRooms, roomId, true);
      leftStagePeers.add(myId);
    }
    let leftStage = !!leftStageRooms[roomId];
    is2(myPeerState, {
      leftStage
    });
    let [isLeftStage, peerId, state] = useEvent(peerState, (peerId2, state2) => (state2 == null ? void 0 : state2.leftStage) === !leftStagePeers.has(peerId2));
    if (isLeftStage) {
      if (state.leftStage) {
        leftStagePeers.add(peerId);
        let iAmModerator = room.moderators.includes(myId);
        if (iAmModerator && room.speakers.includes(peerId)) {
          removeSpeaker({
            myIdentity
          }, roomId, peerId);
        }
      } else {
        leftStagePeers.delete(peerId);
      }
    }
    speakers = useStableArray(speakers.filter(s3 => !leftStagePeers.has(s3)));
    return speakers;
  };
}
function getCachedRoom(roomId) {
  if (!roomId) return null;
  return getCache(`${apiUrl()}/rooms/${roomId}`).data;
}
async function addSpeaker(state, roomId, peerId) {
  let room = getCachedRoom(roomId);
  if (room === null) return false;
  let {
    speakers = []
  } = room;
  if (speakers.includes(peerId)) return true;
  let newRoom = {
    ...room,
    speakers: [...speakers, peerId]
  };
  return await put(state, `/rooms/${roomId}`, newRoom);
}
async function removeSpeaker(state, roomId, peerId) {
  let room = getCachedRoom(roomId);
  if (room === null) return false;
  let {
    speakers = []
  } = room;
  if (!speakers.includes(peerId)) return true;
  let newRoom = {
    ...room,
    speakers: speakers.filter(id => id !== peerId)
  };
  return await put(state, `/rooms/${roomId}`, newRoom);
}

// ../../jam-core/room/Presenters.js
function getCachedRoom2(roomId) {
  if (!roomId) return null;
  return getCache(`${apiUrl()}/rooms/${roomId}`).data;
}
async function addPresenter(state, roomId, peerId) {
  let room = getCachedRoom2(roomId);
  if (room === null) return false;
  let {
    presenters = []
  } = room;
  if (presenters.includes(peerId)) return true;
  let newRoom = {
    ...room,
    presenters: [...presenters, peerId]
  };
  return await put(state, `/rooms/${roomId}`, newRoom);
}
async function removePresenter(state, roomId, peerId) {
  let room = getCachedRoom2(roomId);
  if (room === null) return false;
  let {
    presenters = []
  } = room;
  if (!presenters.includes(peerId)) return true;
  let newRoom = {
    ...room,
    presenters: presenters.filter(id => id !== peerId)
  };
  return await put(state, `/rooms/${roomId}`, newRoom);
}

// ../../jam-core/room.js
function RoomState(_ref9) {
  let {
    roomId,
    myIdentity,
    peerState,
    myPeerState
  } = _ref9;
  var _a4, _b;
  const path = roomId && `${apiUrl()}/rooms/${roomId}`;
  let {
    data,
    isLoading
  } = use(GetRequest, {
    path
  });
  let hasRoom = !!data;
  let room = data != null ? data : emptyRoom;
  let {
    moderators,
    presenters,
    stageOnly,
    videoCall,
    videoEnabled
  } = room;
  let myId = myIdentity.publicKey;
  let accessRestricted = !!((_a4 = room.access) == null ? void 0 : _a4.identities);
  let speakers = use(Speakers, {
    roomId,
    hasRoom,
    room,
    peerState,
    myPeerState,
    myIdentity
  });
  room = useStableObject({
    ...room,
    speakers
  });
  let iAmModerator = moderators.includes(myId);
  let iAmSpeaker = !!stageOnly || speakers.includes(myId);
  let iAmPresenter = !!videoCall || presenters && presenters.includes(myId) || videoEnabled && iAmSpeaker;
  let iAmAuthorized = !accessRestricted || ((_b = room.access) == null ? void 0 : _b.identities.includes(myId));
  return {
    roomId,
    room,
    hasRoom,
    isRoomLoading: isLoading,
    iAmSpeaker,
    iAmModerator,
    iAmAuthorized,
    iAmPresenter
  };
}
var _a3;
var emptyRoom = {
  name: "",
  description: "",
  ...((_a3 = staticConfig.defaultRoom) != null ? _a3 : null),
  speakers: [],
  moderators: [],
  presenters: []
};
function getCachedRoom3(roomId) {
  if (!roomId) return null;
  return getCache(`${apiUrl()}/rooms/${roomId}`).data;
}
async function addModerator(state, roomId, peerId) {
  let room = getCachedRoom3(roomId);
  if (room === null) return false;
  let {
    moderators = []
  } = room;
  if (moderators.includes(peerId)) return true;
  let newRoom = {
    ...room,
    moderators: [...moderators, peerId]
  };
  return await put(state, `/rooms/${roomId}`, newRoom);
}
async function removeModerator(state, roomId, peerId) {
  let room = getCachedRoom3(roomId);
  if (room === null) return false;
  let {
    moderators = []
  } = room;
  if (!moderators.includes(peerId)) return true;
  let newRoom = {
    ...room,
    moderators: moderators.filter(id => id !== peerId)
  };
  return await put(state, `/rooms/${roomId}`, newRoom);
}

// ../../jam-core/backend.js
var API = `${staticConfig.urls.pantry}/api/v1`;
on4(staticConfig, () => {
  API = `${staticConfig.urls.pantry}/api/v1`;
});
function apiUrl() {
  return API;
}
function populateApiCache(path, data) {
  populateCache(API + path, data);
}
async function authenticatedApiRequest(_ref10, method, path, payload) {
  let {
    myIdentity
  } = _ref10;
  let res = await fetch(API + path, {
    method: method.toUpperCase(),
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json"
    },
    body: payload ? JSON.stringify(await signData2(myIdentity, payload)) : void 0
  });
  return res.ok;
}
async function get(path) {
  let res = await fetch(API + path, {
    method: "GET",
    headers: {
      Accept: "application/json"
    }
  });
  if (res.status < 400) return [await res.json(), true, res.status];else return [void 0, false, res.status];
}
async function post(state, path, payload) {
  return authenticatedApiRequest(state, "POST", path, payload);
}
async function put(state, path, payload) {
  return authenticatedApiRequest(state, "PUT", path, payload);
}
async function putOrPost(state, path, payload) {
  return (await put(state, path, payload)) || (await post(state, path, payload));
}
async function deleteRequest(state, path, payload) {
  if (payload === void 0) {
    payload = null;
  }
  return authenticatedApiRequest(state, "DELETE", path, payload);
}
async function createRoom(state, roomId, room) {
  if (room === void 0) {
    room = {};
  }
  const {
    name = "",
    description = "",
    logoURI = void 0,
    color = void 0,
    stageOnly = false,
    videoCall = false,
    videoEnabled = false
  } = room;
  let {
    myId
  } = state;
  let newRoom = {
    ...emptyRoom,
    ...room,
    name,
    description,
    logoURI,
    color,
    stageOnly: !!stageOnly,
    videoCall: !!videoCall,
    videoEnabled: !!videoEnabled,
    moderators: [myId],
    speakers: [myId]
  };
  let ok = await post(state, `/rooms/${roomId}`, newRoom);
  if (ok) populateCache(API + `/rooms/${roomId}`, newRoom);
  return ok;
}
async function updateRoom(state, roomId, room) {
  if (!roomId || !room) return false;
  if (!(room == null ? void 0 : room.moderators) || !(room == null ? void 0 : room.speakers)) return false;
  return await put(state, `/rooms/${roomId}`, room);
}
async function getRoom(roomId) {
  if (!roomId) return void 0;
  return (await get(`/rooms/${roomId}`))[0];
}
async function recordingsDownloadLink(_ref11, roomId) {
  let {
    myIdentity
  } = _ref11;
  return `${API}/rooms/${roomId}/recordings.zip?token=${await signedToken(myIdentity)}`;
}

// ../../lib/swarm.js
import { emit as emit5, on as on6, is as is4, clear as clear3, update as update4 } from "minimal-state";

// ../../lib/signalws.js
import { clear as clear2, emit as emit2, is as is3, on as on5, until } from "minimal-state";
var PING_INTERVAL = 5e3;
async function signalws(_ref12) {
  let {
    url,
    roomId,
    myPeerId,
    myConnId,
    sign: sign2,
    subscriptions = []
  } = _ref12;
  if (!url) throw new Error("signaling url required");
  if (!roomId) throw new Error("room id required");
  if (!myPeerId) throw new Error("peer id required");
  if (!myConnId) myConnId = String(Math.random()).slice(2, 10);
  url = url.indexOf("://") === -1 ? "wss://" + url : url;
  url = url.replace("http", "ws");
  if (!url.endsWith("/")) url += "/";
  let token = encode(new TextEncoder().encode(JSON.stringify(await sign2({}))));
  let subs = subscriptions.join(",");
  url += `${roomId}?id=${myPeerId}.${myConnId}&token=${token}&subs=${subs}`;
  let ws = new WebSocket(url);
  ws.addEventListener("open", () => {
    is3(hub, "opened", true);
    hub.interval = setInterval(() => {
      send(hub, {
        t: "ping"
      });
    }, PING_INTERVAL);
  });
  ws.addEventListener("message", _ref13 => {
    let {
      data
    } = _ref13;
    let msg = parse2(data);
    if (msg === void 0) return;
    let {
      t: topic,
      d,
      p,
      r: requestId
    } = msg;
    if (window.DEBUG && (d == null ? void 0 : d.peerId) !== myPeerId) {
      console.log("ws message", data);
    }
    if (topic === "opened" || topic === "closed") return;
    let payload = d != null ? d : {};
    if (p) {
      let [peerId, connId] = p.split(".");
      payload.peerId = peerId;
      payload.connId = connId;
    }
    if (topic === "response") {
      requestAccepted(requestId, payload);
      return;
    } else if (topic === "server") {
      let accept = requestId ? data2 => sendAccept(hub, requestId, data2) : () => {};
      emit2(hub, topic, payload, accept);
    } else {
      emit2(hub, topic, payload);
    }
  });
  ws.addEventListener("error", () => {
    if (window.DEBUG) {
      console.error("ws error");
    }
  });
  ws.addEventListener("close", () => {
    if (window.DEBUG) console.log("ws closed");
    is3(hub, "closed", true);
    clear2(hub);
    clearInterval(hub.interval);
  });
  const hub = {
    opened: false,
    closed: false,
    interval: null,
    myPeerId,
    ws,
    subs: subscriptions,
    subscribe(topic, onMessage) {
      return subscribe2(hub, topic, onMessage);
    },
    broadcast(topic, message) {
      if (message === void 0) {
        message = {};
      }
      return broadcast(hub, topic, message);
    },
    sendDirect(receiverId, message) {
      if (message === void 0) {
        message = {};
      }
      return sendDirect(hub, receiverId, message);
    },
    sendRequest(topic, message) {
      if (message === void 0) {
        message = {};
      }
      return sendRequest(hub, topic, message);
    },
    close() {
      close(hub);
    }
  };
  return hub;
}
function subscribe2(hub, topic, onMessage) {
  let {
    subs
  } = hub;
  if (!subs.includes(topic)) {
    subs.push(topic);
    until(hub, "opened").then(() => send(hub, {
      s: topic
    }));
  }
  return on5(hub, topic, data => onMessage(data));
}
async function broadcast(hub, topic, message) {
  await until(hub, "opened");
  return send(hub, {
    t: topic,
    d: message
  });
}
async function sendDirect(hub, _ref14, message) {
  let {
    peerId,
    connId
  } = _ref14;
  await until(hub, "opened");
  return send(hub, {
    t: "direct",
    d: message,
    p: `${peerId}.${connId}`
  });
}
async function sendRequest(hub, topic, message) {
  await until(hub, "opened");
  let {
    id,
    promise
  } = newRequest();
  send(hub, {
    t: topic,
    d: message,
    r: id
  });
  return promise;
}
async function sendAccept(hub, requestId, message) {
  return send(hub, {
    t: "response",
    d: message,
    r: requestId
  });
}
function close(_ref15, code) {
  let {
    ws,
    closed
  } = _ref15;
  if (code === void 0) {
    code = 1e3;
  }
  if (!closed) ws.close(code);
}
function send(hub, msg) {
  let {
    ws
  } = hub;
  let jsonMsg = JSON.stringify(msg);
  if (window.DEBUG && (msg == null ? void 0 : msg.t) !== "ping") console.log("ws sending", jsonMsg);
  try {
    ws.send(jsonMsg);
    return true;
  } catch (err) {
    if (window.DEBUG) {
      console.error("ws error");
      console.error(err);
    }
    close(hub);
    return false;
  }
}
function parse2(data) {
  try {
    return JSON.parse(data);
  } catch (err) {
    return void 0;
  }
}
var REQUEST_TIMEOUT = 2e4;
var clientId = Math.random().toString(32).slice(2, 12);
var requests = new Map();
var nextRequestId = 0;
function newRequest(timeout3) {
  if (timeout3 === void 0) {
    timeout3 = REQUEST_TIMEOUT;
  }
  let requestId = `${clientId};${nextRequestId++}`;
  const request = {
    id: requestId
  };
  request.promise = new Promise((resolve, reject) => {
    request.accept = data => {
      clearTimeout(request.timeout);
      resolve(data);
    };
    request.timeout = setTimeout(() => {
      reject(new Error("request timeout"));
    }, timeout3);
  });
  requests.set(requestId, request);
  return request;
}
function requestAccepted(requestId, data) {
  let request = requests.get(requestId);
  if (request === void 0) return;
  request.accept(data);
  requests.delete(requestId);
}

// ../../lib/swarm-health.js
var INITIAL = 0;
var CONNECTING = 1;
var CONNECTED = 2;
var DISCONNECTED = 3;
function checkWsHealth(swarm) {
  setInterval(() => {
    var _a4, _b;
    if (swarm.connectState === INITIAL || swarm.connectState === CONNECTING) return;
    if (!navigator.onLine) return;
    switch ((_b = (_a4 = swarm.hub) == null ? void 0 : _a4.ws) == null ? void 0 : _b.readyState) {
      case void 0:
      case WebSocket.CLOSING:
      case WebSocket.CLOSED:
        swarm.connectState = DISCONNECTED;
        swarm.connect();
    }
  }, 1e3);
}

// ../../lib/simple-peer-light.js
var MAX_BUFFERED_AMOUNT = 64 * 1024;
var ICECOMPLETE_TIMEOUT = 5 * 1e3;
var CHANNEL_CLOSING_TIMEOUT = 5 * 1e3;
function randombytes(size) {
  const array = new Uint8Array(size);
  for (let i = 0; i < size; i++) {
    array[i] = Math.random() * 256 | 0;
  }
  return array;
}
function getBrowserRTC() {
  if (typeof globalThis === "undefined") return null;
  const wrtc = {
    RTCPeerConnection: globalThis.RTCPeerConnection || globalThis.mozRTCPeerConnection || globalThis.webkitRTCPeerConnection,
    RTCSessionDescription: globalThis.RTCSessionDescription || globalThis.mozRTCSessionDescription || globalThis.webkitRTCSessionDescription,
    RTCIceCandidate: globalThis.RTCIceCandidate || globalThis.mozRTCIceCandidate || globalThis.webkitRTCIceCandidate
  };
  if (!wrtc.RTCPeerConnection) return null;
  return wrtc;
}
function errCode(err, code) {
  Object.defineProperty(err, "code", {
    value: code,
    enumerable: true,
    configurable: true
  });
  return err;
}
function filterTrickle(sdp) {
  return sdp.replace(/a=ice-options:trickle\s\n/g, "");
}
function warn(message) {
  console.warn(message);
}
var Peer = class {
  constructor(opts) {
    if (opts === void 0) {
      opts = {};
    }
    this._map = new Map();
    this._id = randombytes(4).toString("hex").slice(0, 7);
    this._doDebug = opts.debug;
    this._debug("new peer %o", opts);
    this.channelName = opts.initiator ? opts.channelName || randombytes(20).toString("hex") : null;
    this.initiator = opts.initiator || false;
    this.channelConfig = opts.channelConfig || Peer.channelConfig;
    this.channelNegotiated = this.channelConfig.negotiated;
    this.config = Object.assign({}, Peer.config, opts.config);
    this.offerOptions = opts.offerOptions || {};
    this.answerOptions = opts.answerOptions || {};
    this.sdpTransform = opts.sdpTransform || (sdp => sdp);
    this.streams = opts.streams || (opts.stream ? [opts.stream] : []);
    this.trickle = opts.trickle !== void 0 ? opts.trickle : true;
    this.allowHalfTrickle = opts.allowHalfTrickle !== void 0 ? opts.allowHalfTrickle : false;
    this.iceCompleteTimeout = opts.iceCompleteTimeout || ICECOMPLETE_TIMEOUT;
    this.destroyed = false;
    this.destroying = false;
    this._connected = false;
    this.remoteAddress = void 0;
    this.remoteFamily = void 0;
    this.remotePort = void 0;
    this.localAddress = void 0;
    this.localFamily = void 0;
    this.localPort = void 0;
    this._wrtc = opts.wrtc && typeof opts.wrtc === "object" ? opts.wrtc : getBrowserRTC();
    if (!this._wrtc) {
      if (typeof window === "undefined") {
        throw errCode(new Error("No WebRTC support: Specify `opts.wrtc` option in this environment"), "ERR_WEBRTC_SUPPORT");
      } else {
        throw errCode(new Error("No WebRTC support: Not a supported browser"), "ERR_WEBRTC_SUPPORT");
      }
    }
    this._pcReady = false;
    this._channelReady = false;
    this._iceComplete = false;
    this._iceCompleteTimer = null;
    this._channel = null;
    this._pendingCandidates = [];
    this._isNegotiating = false;
    this._firstNegotiation = true;
    this._batchedNegotiation = false;
    this._queuedNegotiation = false;
    this._sendersAwaitingStable = [];
    this._senderMap = new Map();
    this._closingInterval = null;
    this._remoteTracks = [];
    this._remoteStreams = [];
    this._chunk = null;
    this._cb = null;
    this._interval = null;
    try {
      this._pc = new this._wrtc.RTCPeerConnection(this.config);
    } catch (err) {
      this.destroy(errCode(err, "ERR_PC_CONSTRUCTOR"));
      return;
    }
    this._isReactNativeWebrtc = typeof this._pc._peerConnectionId === "number";
    this._pc.oniceconnectionstatechange = () => {
      this._onIceStateChange();
    };
    this._pc.onicegatheringstatechange = () => {
      this._onIceStateChange();
    };
    this._pc.onconnectionstatechange = () => {
      this._onConnectionStateChange();
    };
    this._pc.onsignalingstatechange = () => {
      this._onSignalingStateChange();
    };
    this._pc.onicecandidate = event2 => {
      this._onIceCandidate(event2);
    };
    if (typeof this._pc.peerIdentity === "object") {
      this._pc.peerIdentity.catch(err => {
        this.destroy(errCode(err, "ERR_PC_PEER_IDENTITY"));
      });
    }
    if (this.initiator || this.channelNegotiated) {
      this._setupData({
        channel: this._pc.createDataChannel(this.channelName, this.channelConfig)
      });
    } else {
      this._pc.ondatachannel = event2 => {
        this._setupData(event2);
      };
    }
    if (this.streams) {
      this.streams.forEach(stream => {
        this.addStream(stream);
      });
    }
    this._pc.ontrack = event2 => {
      this._onTrack(event2);
    };
    this._debug("initial negotiation");
    this._needsNegotiation();
  }
  get bufferSize() {
    return this._channel && this._channel.bufferedAmount || 0;
  }
  get connected() {
    return this._connected && this._channel.readyState === "open";
  }
  address() {
    return {
      port: this.localPort,
      family: this.localFamily,
      address: this.localAddress
    };
  }
  signal(data) {
    if (this.destroying) return;
    if (this.destroyed) throw errCode(new Error("cannot signal after peer is destroyed"), "ERR_DESTROYED");
    if (typeof data === "string") {
      try {
        data = JSON.parse(data);
      } catch (err) {
        data = {};
      }
    }
    this._debug("signal()");
    if (data.renegotiate && this.initiator) {
      this._debug("got request to renegotiate");
      this._needsNegotiation();
    }
    if (data.transceiverRequest && this.initiator) {
      this._debug("got request for transceiver");
      this.addTransceiver(data.transceiverRequest.kind, data.transceiverRequest.init);
    }
    if (data.candidate) {
      if (this._pc.remoteDescription && this._pc.remoteDescription.type) {
        this._addIceCandidate(data.candidate);
      } else {
        this._pendingCandidates.push(data.candidate);
      }
    }
    if (data.sdp) {
      this._pc.setRemoteDescription(new this._wrtc.RTCSessionDescription(data)).then(() => {
        if (this.destroyed) return;
        this._pendingCandidates.forEach(candidate => {
          this._addIceCandidate(candidate);
        });
        this._pendingCandidates = [];
        if (this._pc.remoteDescription.type === "offer") this._createAnswer();
      }).catch(err => {
        this.destroy(errCode(err, "ERR_SET_REMOTE_DESCRIPTION"));
      });
    }
    if (!data.sdp && !data.candidate && !data.renegotiate && !data.transceiverRequest) {
      this.destroy(errCode(new Error("signal() called with invalid signal data"), "ERR_SIGNALING"));
    }
  }
  _addIceCandidate(candidate) {
    const iceCandidateObj = new this._wrtc.RTCIceCandidate(candidate);
    this._pc.addIceCandidate(iceCandidateObj).catch(err => {
      if (!iceCandidateObj.address || iceCandidateObj.address.endsWith(".local")) {
        warn("Ignoring unsupported ICE candidate.");
      } else {
        this.destroy(errCode(err, "ERR_ADD_ICE_CANDIDATE"));
      }
    });
  }
  send(chunk) {
    if (this.destroying) return;
    if (this.destroyed) throw errCode(new Error("cannot send after peer is destroyed"), "ERR_DESTROYED");
    this._channel.send(chunk);
  }
  addTransceiver(kind, init) {
    if (this.destroying) return;
    if (this.destroyed) throw errCode(new Error("cannot addTransceiver after peer is destroyed"), "ERR_DESTROYED");
    this._debug("addTransceiver()");
    if (this.initiator) {
      try {
        this._pc.addTransceiver(kind, init);
        this._needsNegotiation();
      } catch (err) {
        this.destroy(errCode(err, "ERR_ADD_TRANSCEIVER"));
      }
    } else {
      this.emit("signal", {
        type: "transceiverRequest",
        transceiverRequest: {
          kind,
          init
        }
      });
    }
  }
  addStream(stream) {
    if (this.destroying) return;
    if (this.destroyed) throw errCode(new Error("cannot addStream after peer is destroyed"), "ERR_DESTROYED");
    this._debug("addStream()");
    stream.getTracks().forEach(track => {
      this.addTrack(track, stream);
    });
  }
  addTrack(track, stream) {
    if (this.destroying) return;
    if (this.destroyed) throw errCode(new Error("cannot addTrack after peer is destroyed"), "ERR_DESTROYED");
    this._debug("addTrack()");
    const submap = this._senderMap.get(track) || new Map();
    let sender = submap.get(stream);
    if (!sender || sender.removed) {
      if (sender == null ? void 0 : sender.removed) console.warn("Track has been removed. You should enable/disable tracks that you want to re-add.");
      sender = this._pc.addTrack(track, stream);
      submap.set(stream, sender);
      this._senderMap.set(track, submap);
      this._needsNegotiation();
    } else {
      throw errCode(new Error("Track has already been added to that stream."), "ERR_SENDER_ALREADY_ADDED");
    }
  }
  replaceTrack(oldTrack, newTrack, stream) {
    if (this.destroying) return;
    if (this.destroyed) throw errCode(new Error("cannot replaceTrack after peer is destroyed"), "ERR_DESTROYED");
    this._debug("replaceTrack()");
    const submap = this._senderMap.get(oldTrack);
    const sender = submap ? submap.get(stream) : null;
    if (!sender) {
      throw errCode(new Error("Cannot replace track that was never added."), "ERR_TRACK_NOT_ADDED");
    }
    if (newTrack) this._senderMap.set(newTrack, submap);
    if (sender.replaceTrack != null) {
      sender.replaceTrack(newTrack);
    } else {
      this.destroy(errCode(new Error("replaceTrack is not supported in this browser"), "ERR_UNSUPPORTED_REPLACETRACK"));
    }
  }
  removeTrack(track, stream) {
    if (this.destroying) return;
    if (this.destroyed) throw errCode(new Error("cannot removeTrack after peer is destroyed"), "ERR_DESTROYED");
    this._debug("removeSender()");
    const submap = this._senderMap.get(track);
    const sender = submap ? submap.get(stream) : null;
    if (!sender) {
      throw errCode(new Error("Cannot remove track that was never added."), "ERR_TRACK_NOT_ADDED");
    }
    try {
      sender.removed = true;
      this._pc.removeTrack(sender);
    } catch (err) {
      if (err.name === "NS_ERROR_UNEXPECTED") {
        this._sendersAwaitingStable.push(sender);
      } else {
        this.destroy(errCode(err, "ERR_REMOVE_TRACK"));
      }
    }
    this._needsNegotiation();
  }
  removeStream(stream) {
    if (this.destroying) return;
    if (this.destroyed) throw errCode(new Error("cannot removeStream after peer is destroyed"), "ERR_DESTROYED");
    this._debug("removeSenders()");
    stream.getTracks().forEach(track => {
      this.removeTrack(track, stream);
    });
  }
  _needsNegotiation() {
    this._debug("_needsNegotiation");
    if (this._batchedNegotiation) return;
    this._batchedNegotiation = true;
    queueMicrotask(() => {
      this._batchedNegotiation = false;
      if (this.initiator || !this._firstNegotiation) {
        this._debug("starting batched negotiation");
        this.negotiate();
      } else {
        this._debug("non-initiator initial negotiation request discarded");
      }
      this._firstNegotiation = false;
    });
  }
  negotiate() {
    if (this.destroying) return;
    if (this.destroyed) throw errCode(new Error("cannot negotiate after peer is destroyed"), "ERR_DESTROYED");
    if (this.initiator) {
      if (this._isNegotiating) {
        this._queuedNegotiation = true;
        this._debug("already negotiating, queueing");
      } else {
        this._debug("start negotiation");
        setTimeout(() => {
          this._createOffer();
        }, 0);
      }
    } else {
      if (this._isNegotiating) {
        this._queuedNegotiation = true;
        this._debug("already negotiating, queueing");
      } else {
        this._debug("requesting negotiation from initiator");
        this.emit("signal", {
          type: "renegotiate",
          renegotiate: true
        });
      }
    }
    this._isNegotiating = true;
  }
  destroy(err) {
    if (this.destroyed || this.destroying) return;
    this.destroying = true;
    this._debug("destroying (error: %s)", err && (err.message || err));
    queueMicrotask(() => {
      this.destroyed = true;
      this.destroying = false;
      this._debug("destroy (error: %s)", err && (err.message || err));
      this._connected = false;
      this._pcReady = false;
      this._channelReady = false;
      this._remoteTracks = null;
      this._remoteStreams = null;
      this._senderMap = null;
      clearInterval(this._closingInterval);
      this._closingInterval = null;
      clearInterval(this._interval);
      this._interval = null;
      this._chunk = null;
      this._cb = null;
      if (this._channel) {
        try {
          this._channel.close();
        } catch (err2) {}
        this._channel.onmessage = null;
        this._channel.onopen = null;
        this._channel.onclose = null;
        this._channel.onerror = null;
      }
      if (this._pc) {
        try {
          this._pc.close();
        } catch (err2) {}
        this._pc.oniceconnectionstatechange = null;
        this._pc.onicegatheringstatechange = null;
        this._pc.onsignalingstatechange = null;
        this._pc.onicecandidate = null;
        this._pc.ontrack = null;
        this._pc.ondatachannel = null;
      }
      this._pc = null;
      this._channel = null;
      if (err) this.emit("error", err);
      this.emit("close");
    });
  }
  _setupData(event2) {
    if (!event2.channel) {
      return this.destroy(errCode(new Error("Data channel event is missing `channel` property"), "ERR_DATA_CHANNEL"));
    }
    this._channel = event2.channel;
    this._channel.binaryType = "arraybuffer";
    if (typeof this._channel.bufferedAmountLowThreshold === "number") {
      this._channel.bufferedAmountLowThreshold = MAX_BUFFERED_AMOUNT;
    }
    this.channelName = this._channel.label;
    this._channel.onmessage = event3 => {
      this._onChannelMessage(event3);
    };
    this._channel.onbufferedamountlow = () => {
      this._onChannelBufferedAmountLow();
    };
    this._channel.onopen = () => {
      this._onChannelOpen();
    };
    this._channel.onclose = () => {
      this._onChannelClose();
    };
    this._channel.onerror = err => {
      this.destroy(errCode(err, "ERR_DATA_CHANNEL"));
    };
    let isClosing = false;
    this._closingInterval = setInterval(() => {
      if (this._channel && this._channel.readyState === "closing") {
        if (isClosing) this._onChannelClose();
        isClosing = true;
      } else {
        isClosing = false;
      }
    }, CHANNEL_CLOSING_TIMEOUT);
  }
  _startIceCompleteTimeout() {
    if (this.destroyed) return;
    if (this._iceCompleteTimer) return;
    this._debug("started iceComplete timeout");
    this._iceCompleteTimer = setTimeout(() => {
      if (!this._iceComplete) {
        this._iceComplete = true;
        this._debug("iceComplete timeout completed");
        this.emit("iceTimeout");
        this.emit("_iceComplete");
      }
    }, this.iceCompleteTimeout);
  }
  _createOffer() {
    if (this.destroyed) return;
    this._pc.createOffer(this.offerOptions).then(offer => {
      if (this.destroyed) return;
      if (!this.trickle && !this.allowHalfTrickle) {
        offer.sdp = filterTrickle(offer.sdp);
      }
      offer.sdp = this.sdpTransform(offer.sdp);
      const sendOffer = () => {
        if (this.destroyed) return;
        const signal = this._pc.localDescription || offer;
        this._debug("signal");
        this.emit("signal", {
          type: signal.type,
          sdp: signal.sdp
        });
      };
      const onSuccess = () => {
        this._debug("createOffer success");
        if (this.destroyed) return;
        if (this.trickle || this._iceComplete) sendOffer();else this.once("_iceComplete", sendOffer);
      };
      const onError = err => {
        this.destroy(errCode(err, "ERR_SET_LOCAL_DESCRIPTION"));
      };
      this._pc.setLocalDescription(offer).then(onSuccess).catch(onError);
    }).catch(err => {
      this.destroy(errCode(err, "ERR_CREATE_OFFER"));
    });
  }
  _requestMissingTransceivers() {
    if (this._pc.getTransceivers) {
      this._pc.getTransceivers().forEach(transceiver => {
        if (!transceiver.mid && transceiver.sender.track && !transceiver.requested) {
          transceiver.requested = true;
          this.addTransceiver(transceiver.sender.track.kind);
        }
      });
    }
  }
  _createAnswer() {
    if (this.destroyed) return;
    this._pc.createAnswer(this.answerOptions).then(answer => {
      if (this.destroyed) return;
      if (!this.trickle && !this.allowHalfTrickle) {
        answer.sdp = filterTrickle(answer.sdp);
      }
      answer.sdp = this.sdpTransform(answer.sdp);
      const sendAnswer = () => {
        if (this.destroyed) return;
        const signal = this._pc.localDescription || answer;
        this._debug("signal");
        this.emit("signal", {
          type: signal.type,
          sdp: signal.sdp
        });
        if (!this.initiator) this._requestMissingTransceivers();
      };
      const onSuccess = () => {
        if (this.destroyed) return;
        if (this.trickle || this._iceComplete) sendAnswer();else this.once("_iceComplete", sendAnswer);
      };
      const onError = err => {
        this.destroy(errCode(err, "ERR_SET_LOCAL_DESCRIPTION"));
      };
      this._pc.setLocalDescription(answer).then(onSuccess).catch(onError);
    }).catch(err => {
      this.destroy(errCode(err, "ERR_CREATE_ANSWER"));
    });
  }
  _onConnectionStateChange() {
    if (this.destroyed) return;
    if (this._pc.connectionState === "failed") {
      this.destroy(errCode(new Error("Connection failed."), "ERR_CONNECTION_FAILURE"));
    }
  }
  _onIceStateChange() {
    if (this.destroyed) return;
    const iceConnectionState = this._pc.iceConnectionState;
    const iceGatheringState = this._pc.iceGatheringState;
    this._debug("iceStateChange (connection: %s) (gathering: %s)", iceConnectionState, iceGatheringState);
    this.emit("iceStateChange", iceConnectionState, iceGatheringState);
    if (iceConnectionState === "connected" || iceConnectionState === "completed") {
      this._pcReady = true;
      this._maybeReady();
    }
    if (iceConnectionState === "failed") {
      this.destroy(errCode(new Error("Ice connection failed."), "ERR_ICE_CONNECTION_FAILURE"));
    }
    if (iceConnectionState === "closed") {
      this.destroy(errCode(new Error("Ice connection closed."), "ERR_ICE_CONNECTION_CLOSED"));
    }
  }
  getStats(cb) {
    const flattenValues = report => {
      if (Object.prototype.toString.call(report.values) === "[object Array]") {
        report.values.forEach(value => {
          Object.assign(report, value);
        });
      }
      return report;
    };
    if (this._pc.getStats.length === 0 || this._isReactNativeWebrtc) {
      this._pc.getStats().then(res => {
        const reports = [];
        res.forEach(report => {
          reports.push(flattenValues(report));
        });
        cb(null, reports);
      }, err => cb(err));
    } else if (this._pc.getStats.length > 0) {
      this._pc.getStats(res => {
        if (this.destroyed) return;
        const reports = [];
        res.result().forEach(result => {
          const report = {};
          result.names().forEach(name => {
            report[name] = result.stat(name);
          });
          report.id = result.id;
          report.type = result.type;
          report.timestamp = result.timestamp;
          reports.push(flattenValues(report));
        });
        cb(null, reports);
      }, err => cb(err));
    } else {
      cb(null, []);
    }
  }
  _maybeReady() {
    this._debug("maybeReady pc %s channel %s", this._pcReady, this._channelReady);
    if (this._connected || this._connecting || !this._pcReady || !this._channelReady) {
      return;
    }
    this._connecting = true;
    const findCandidatePair = () => {
      if (this.destroyed) return;
      this.getStats((err, items) => {
        if (this.destroyed) return;
        if (err) items = [];
        const remoteCandidates = {};
        const localCandidates = {};
        const candidatePairs = {};
        let foundSelectedCandidatePair = false;
        items.forEach(item => {
          if (item.type === "remotecandidate" || item.type === "remote-candidate") {
            remoteCandidates[item.id] = item;
          }
          if (item.type === "localcandidate" || item.type === "local-candidate") {
            localCandidates[item.id] = item;
          }
          if (item.type === "candidatepair" || item.type === "candidate-pair") {
            candidatePairs[item.id] = item;
          }
        });
        const setSelectedCandidatePair = selectedCandidatePair => {
          foundSelectedCandidatePair = true;
          let local = localCandidates[selectedCandidatePair.localCandidateId];
          if (local && (local.ip || local.address)) {
            this.localAddress = local.ip || local.address;
            this.localPort = Number(local.port);
          } else if (local && local.ipAddress) {
            this.localAddress = local.ipAddress;
            this.localPort = Number(local.portNumber);
          } else if (typeof selectedCandidatePair.googLocalAddress === "string") {
            local = selectedCandidatePair.googLocalAddress.split(":");
            this.localAddress = local[0];
            this.localPort = Number(local[1]);
          }
          if (this.localAddress) {
            this.localFamily = this.localAddress.includes(":") ? "IPv6" : "IPv4";
          }
          let remote = remoteCandidates[selectedCandidatePair.remoteCandidateId];
          if (remote && (remote.ip || remote.address)) {
            this.remoteAddress = remote.ip || remote.address;
            this.remotePort = Number(remote.port);
          } else if (remote && remote.ipAddress) {
            this.remoteAddress = remote.ipAddress;
            this.remotePort = Number(remote.portNumber);
          } else if (typeof selectedCandidatePair.googRemoteAddress === "string") {
            remote = selectedCandidatePair.googRemoteAddress.split(":");
            this.remoteAddress = remote[0];
            this.remotePort = Number(remote[1]);
          }
          if (this.remoteAddress) {
            this.remoteFamily = this.remoteAddress.includes(":") ? "IPv6" : "IPv4";
          }
          this._debug("connect local: %s:%s remote: %s:%s", this.localAddress, this.localPort, this.remoteAddress, this.remotePort);
        };
        items.forEach(item => {
          if (item.type === "transport" && item.selectedCandidatePairId) {
            setSelectedCandidatePair(candidatePairs[item.selectedCandidatePairId]);
          }
          if (item.type === "googCandidatePair" && item.googActiveConnection === "true" || (item.type === "candidatepair" || item.type === "candidate-pair") && item.selected) {
            setSelectedCandidatePair(item);
          }
        });
        if (!foundSelectedCandidatePair && (!Object.keys(candidatePairs).length || Object.keys(localCandidates).length)) {
          setTimeout(findCandidatePair, 100);
          return;
        } else {
          this._connecting = false;
          this._connected = true;
        }
        if (this._chunk) {
          try {
            this.send(this._chunk);
          } catch (err2) {
            return this.destroy(errCode(err2, "ERR_DATA_CHANNEL"));
          }
          this._chunk = null;
          this._debug('sent chunk from "write before connect"');
          const cb = this._cb;
          this._cb = null;
          cb(null);
        }
        if (typeof this._channel.bufferedAmountLowThreshold !== "number") {
          this._interval = setInterval(() => this._onInterval(), 150);
          if (this._interval.unref) this._interval.unref();
        }
        this._debug("connect");
        this.emit("connect");
      });
    };
    findCandidatePair();
  }
  _onInterval() {
    if (!this._cb || !this._channel || this._channel.bufferedAmount > MAX_BUFFERED_AMOUNT) {
      return;
    }
    this._onChannelBufferedAmountLow();
  }
  _onSignalingStateChange() {
    if (this.destroyed) return;
    if (this._pc.signalingState === "stable") {
      this._isNegotiating = false;
      this._debug("flushing sender queue", this._sendersAwaitingStable);
      this._sendersAwaitingStable.forEach(sender => {
        this._pc.removeTrack(sender);
        this._queuedNegotiation = true;
      });
      this._sendersAwaitingStable = [];
      if (this._queuedNegotiation) {
        this._debug("flushing negotiation queue");
        this._queuedNegotiation = false;
        this._needsNegotiation();
      } else {
        this._debug("negotiated");
        this.emit("negotiated");
      }
    }
    this._debug("signalingStateChange %s", this._pc.signalingState);
    this.emit("signalingStateChange", this._pc.signalingState);
  }
  _onIceCandidate(event2) {
    if (this.destroyed) return;
    if (event2.candidate && this.trickle) {
      this.emit("signal", {
        type: "candidate",
        candidate: {
          candidate: event2.candidate.candidate,
          sdpMLineIndex: event2.candidate.sdpMLineIndex,
          sdpMid: event2.candidate.sdpMid
        }
      });
    } else if (!event2.candidate && !this._iceComplete) {
      this._iceComplete = true;
      this.emit("_iceComplete");
    }
    if (event2.candidate) {
      this._startIceCompleteTimeout();
    }
  }
  _onChannelMessage(event2) {
    if (this.destroyed) return;
    let data = event2.data;
    if (data instanceof ArrayBuffer) data = new Uint8Array(data);
    this.emit("data", data);
  }
  _onChannelBufferedAmountLow() {
    if (this.destroyed || !this._cb) return;
    this._debug("ending backpressure: bufferedAmount %d", this._channel.bufferedAmount);
    const cb = this._cb;
    this._cb = null;
    cb(null);
  }
  _onChannelOpen() {
    if (this._connected || this.destroyed) return;
    this._debug("on channel open");
    this._channelReady = true;
    this._maybeReady();
  }
  _onChannelClose() {
    if (this.destroyed) return;
    this._debug("on channel close");
    this.destroy();
  }
  _onTrack(event2) {
    if (this.destroyed) return;
    event2.streams.forEach(eventStream => {
      this._debug("on track");
      this.emit("track", event2.track, eventStream);
      this._remoteTracks.push({
        track: event2.track,
        stream: eventStream
      });
      if (this._remoteStreams.some(remoteStream => {
        return remoteStream.id === eventStream.id;
      })) {
        return;
      }
      this._remoteStreams.push(eventStream);
      queueMicrotask(() => {
        this._debug("on stream");
        this.emit("stream", eventStream);
      });
    });
  }
  _debug() {
    if (!this._doDebug) return;
    for (var _len7 = arguments.length, args = new Array(_len7), _key7 = 0; _key7 < _len7; _key7++) {
      args[_key7] = arguments[_key7];
    }
    args[0] = "[" + this._id + "] " + args[0];
    console.log(...args);
  }
  on(key, listener) {
    const map = this._map;
    if (!map.has(key)) map.set(key, new Set());
    map.get(key).add(listener);
  }
  off(key, listener) {
    const map = this._map;
    const listeners = map.get(key);
    if (!listeners) return;
    listeners.delete(listener);
    if (listeners.size === 0) map.delete(key);
  }
  once(key, listener) {
    var _this = this;
    const listener_ = function () {
      _this.off(key, listener_);
      listener(...arguments);
    };
    this.on(key, listener_);
  }
  emit(key) {
    const map = this._map;
    if (!map.has(key)) return;
    for (var _len8 = arguments.length, args = new Array(_len8 > 1 ? _len8 - 1 : 0), _key8 = 1; _key8 < _len8; _key8++) {
      args[_key8 - 1] = arguments[_key8];
    }
    for (const listener of map.get(key)) {
      try {
        listener(...args);
      } catch (err) {
        console.error(err);
      }
    }
  }
};
Peer.WEBRTC_SUPPORT = !!getBrowserRTC();
Peer.config = {
  iceServers: [{
    urls: ["stun:stun.l.google.com:19302", "stun:global.stun.twilio.com:3478"]
  }],
  sdpSemantics: "unified-plan"
};
Peer.channelConfig = {};
var simple_peer_light_default = Peer;

// ../../lib/swarm-peer.js
import { emit as emit3, set as set4 } from "minimal-state";

// ../../lib/timeout.js
function timeout2(object, delay, handler) {
  var _a4, _b;
  let context = timeoutContexts.get(object);
  if (context === void 0) {
    context = {};
    timeoutContexts.set(object, context);
  }
  clearTimeout(context.timeout);
  let now = Date.now();
  context.handler = handler != null ? handler : context.handler;
  context.timeoutStart = (_a4 = context.timeoutStart) != null ? _a4 : now;
  delay = Math.max(0, ((_b = context.timeoutEnd) != null ? _b : 0) - now, delay);
  context.timeoutEnd = now + delay;
  context.timeout = setTimeout(() => {
    var _a5;
    let totalDuration = Date.now() - context.timeoutStart;
    context.timeout = null;
    context.timeoutStart = null;
    context.timeoutEnd = null;
    (_a5 = context.handler) == null ? void 0 : _a5.call(context, totalDuration);
  }, delay);
}
function addTimeout(object, delay) {
  let context = timeoutContexts.get(object);
  if (context == null ? void 0 : context.timeout) timeout2(object, delay);
}
function stopTimeout(object) {
  let context = timeoutContexts.get(object);
  if (context === void 0) return;
  clearTimeout(context.timeout);
  context.timeout = null;
  context.timeoutStart = null;
  context.timeoutEnd = null;
}
var timeoutContexts = new WeakMap();

// ../../lib/swarm-peer.js
var MAX_CONNECT_TIME = 6e3;
var MAX_CONNECT_TIME_AFTER_ICE_DISCONNECT = 2e3;
var MIN_MAX_CONNECT_TIME_AFTER_SIGNAL = 2e3;
var MAX_FAIL_TIME = Infinity;
function newConnection(_ref16) {
  let {
    swarm,
    peerId,
    connId
  } = _ref16;
  return {
    swarm,
    peerId,
    connId,
    lastFailure: null
  };
}
function connectPeer(connection) {
  let {
    swarm,
    peerId,
    connId
  } = connection;
  log3("connecting peer", s(peerId), connId);
  if (swarm.hub === null) return;
  let {
    myPeerId,
    myConnId
  } = swarm;
  timeoutPeer(connection, MAX_CONNECT_TIME);
  if (myPeerId > peerId || myPeerId === peerId && myConnId > connId) {
    log3("i initiate, and override any previous peer!");
    createPeer(connection, true);
  } else {
    log3("i dont initiate, wait for signal");
    swarm.hub.sendDirect(connection, {
      type: "signal",
      data: {
        youStart: true,
        type: "you-start"
      }
    });
    log3("sent you-start", s(peerId));
  }
}
function disconnectPeer(connection) {
  let {
    swarm,
    peerId,
    connId
  } = connection;
  log3("disconnecting peer", s(peerId), connId);
  stopTimeout(connection);
  let {
    remoteStreams,
    connectionStreams
  } = swarm;
  if (connectionStreams.find(s3 => s3.peerId === peerId && s3.connId === connId)) {
    connectionStreams = connectionStreams.filter(s3 => s3.peerId !== peerId || s3.connId !== connId);
    remoteStreams = [...remoteStreams];
    for (let i = remoteStreams.length - 1; i >= 0; i--) {
      if (remoteStreams[i].peerId === peerId) {
        let newRemoteStream = void 0;
        let latestTime = -1;
        for (let s3 of connectionStreams) {
          if (s3.peerId === peerId && s3.name === remoteStreams[i].name) {
            if (s3.time > latestTime) {
              newRemoteStream = s3;
              latestTime = s3.time;
            }
          }
        }
        if (newRemoteStream !== void 0) {
          remoteStreams[i] = newRemoteStream;
        } else {
          remoteStreams.splice(i, 1);
        }
      }
    }
    set4(swarm, {
      remoteStreams,
      connectionStreams
    });
  }
  let {
    pc
  } = connection;
  if (pc !== void 0) {
    pc.garbage = true;
    try {
      pc.destroy();
    } catch (e) {}
  }
}
function handleSignal(connection, _ref17) {
  let {
    data
  } = _ref17;
  let {
    swarm,
    peerId,
    connId
  } = connection;
  let {
    myConnId,
    myPeerId
  } = swarm;
  if (data.youStart) {
    log3("i initiate, but dont override if i already have a peer");
    if (!connection.pc) {
      log3("creating peer because i didnt have one");
      createPeer(connection, true);
    } else {
      log3("not creating peer");
    }
    return;
  }
  let peer = connection.pc;
  let {
    first,
    from
  } = data;
  let iAmActive = myPeerId > peerId || myPeerId === peerId && myConnId > connId;
  if (first && !iAmActive) {
    log3("I got the first signal and create a new peer to receive it");
    peer = createPeer(connection, false);
    peer.from = from;
  }
  if (!peer || peer.destroyed) {
    let pcUid = `${peerId}.${connId}.${from}`;
    if (!rejectedPeers.has(pcUid)) {
      rejectedPeers.add(pcUid);
      console.warn("I received a signal without being in a valid state for any further action. Reconnecting!");
      log3("Signal data:", data);
      connectPeer(connection);
    } else {
      console.warn("Ignoring another signal from same peer in invalid state");
    }
    return;
  }
  if (!peer.from) {
    peer.from = from;
    if (!iAmActive) console.error("Something impossible happened");
  }
  if (peer.from !== from) {
    console.warn("Ignoring signal from wrong peer instance.");
    return;
  }
  addPeerMetaData(peer, data.meta);
  peer.signal(data);
  if (!(first && !iAmActive)) addTimeout(connection, MIN_MAX_CONNECT_TIME_AFTER_SIGNAL);
}
var rejectedPeers = new Set();
function createPeer(connection, initiator) {
  let {
    swarm,
    peerId,
    connId
  } = connection;
  let {
    hub,
    localStreams,
    pcConfig
  } = swarm;
  let peer = connection.pc;
  if (peer) {
    log3("destroying old peer", s(peerId));
    peer.garbage = true;
    peer.destroy();
  }
  log3("creating peer", s(peerId), connId);
  let streams = Object.values(localStreams).filter(x => x);
  peer = new simple_peer_light_default({
    initiator,
    config: pcConfig || void 0,
    trickle: true,
    streams
  });
  peer.peerId = peerId;
  peer.connId = connId;
  peer.streams = {
    ...localStreams
  };
  connection.pc = peer;
  peer.connectStart = Date.now();
  peer.didSignal = false;
  peer.on("signal", data => {
    log3("signaling to", s(peerId), connId, data.type);
    let remoteStreamIds = [];
    for (let name in peer.streams) {
      let stream = peer.streams[name];
      if (stream) {
        remoteStreamIds.push([name, stream.id]);
      }
    }
    data.meta = {
      remoteStreamIds
    };
    data.from = peer._id;
    if (!peer.didSignal) {
      data.first = true;
      peer.didSignal = true;
    }
    hub.sendDirect(connection, {
      type: "signal",
      data
    });
  });
  peer.on("connect", () => {
    log3("connected peer", s(peerId), "after", Date.now() - peer.connectStart);
    handlePeerSuccess(connection);
  });
  peer.on("data", rawData => {
    log3("data (channel) from", s(peerId));
    emit3(swarm, "data", rawData);
  });
  peer.on("track", (track, stream) => {
    var _a4, _b;
    log3("onTrack", track, stream);
    let name = (_b = (_a4 = peer.remoteStreamIds) == null ? void 0 : _a4.find(_ref18 => {
      let [, id] = _ref18;
      return id === stream.id;
    })) == null ? void 0 : _b[0];
    let time = Date.now();
    let connectionStreams = [...swarm.connectionStreams];
    let i = connectionStreams.findIndex(s3 => s3.peerId === peerId && s3.connId === connId && s3.name === name);
    if (i === -1) i = connectionStreams.length;
    connectionStreams[i] = {
      stream,
      name,
      peerId,
      connId,
      time
    };
    let remoteStreams = [...swarm.remoteStreams];
    i = remoteStreams.findIndex(streamObj => streamObj.peerId === peerId && streamObj.name === name);
    if (i === -1) i = remoteStreams.length;
    remoteStreams[i] = {
      stream,
      name,
      peerId
    };
    stream.onremovetrack = () => {
      if (!stream.getTracks().length) {
        console.log(`Stream ${stream.id} removed.`);
        const connectionStreams2 = [...swarm.connectionStreams].filter(s3 => !(s3.peerId === peerId && s3.connId === connId && s3.name === name));
        const remoteStreams2 = [...swarm.remoteStreams].filter(streamObj => !(streamObj.peerId === peerId && streamObj.name === name));
        set4(swarm, {
          remoteStreams: remoteStreams2,
          connectionStreams: connectionStreams2
        });
        emit3(swarm, "stream", {
          stream,
          name,
          connection
        });
      }
    };
    set4(swarm, {
      remoteStreams,
      connectionStreams
    });
    emit3(swarm, "stream", {
      stream,
      name,
      connection
    });
  });
  peer.on("error", err => {
    if (peer.garbage) return;
    log3("simplepeer error", err);
    let errCode2 = err == null ? void 0 : err.code;
    switch (errCode2) {
      case "ERR_ICE_CONNECTION_FAILURE":
      case "ERR_ICE_CONNECTION_CLOSED":
        break;
      case "ERR_DATA_CHANNEL":
        timeoutPeer(connection, MAX_CONNECT_TIME_AFTER_ICE_DISCONNECT);
        break;
      default:
        timeoutPeer(connection, MAX_CONNECT_TIME);
    }
  });
  peer.on("iceStateChange", state => {
    log3("ice state", state);
    if (state === "disconnected") {
      timeoutPeer(connection, MAX_CONNECT_TIME_AFTER_ICE_DISCONNECT);
    }
    if (state === "failed" || state === "closed") {
      handlePeerFail(connection);
    }
    if (state === "connected" || state === "completed") {
      handlePeerSuccess(connection);
    }
  });
  return peer;
}
function timeoutPeer(connection, delay) {
  connection.lastFailure = connection.lastFailure || Date.now();
  timeout2(connection, delay, () => {
    if (connection.swarm.connectState === DISCONNECTED || connection.swarm.connectState === INITIAL) {
      timeoutPeer(connection, MAX_CONNECT_TIME);
      return;
    }
    log3("peer timed out");
    handlePeerFail(connection);
  });
}
function handlePeerSuccess(connection) {
  connection.lastFailure = null;
  stopTimeout(connection);
}
function handlePeerFail(connection, forcedFail) {
  stopTimeout(connection);
  let now = Date.now();
  connection.lastFailure = connection.lastFailure || now;
  let failTime = now - connection.lastFailure;
  log3("handle peer fail! time failing:", failTime);
  if (forcedFail === true || failTime > MAX_FAIL_TIME) {
    disconnectPeer(connection);
  } else {
    connectPeer(connection);
  }
}
function addPeerMetaData(peer, data) {
  if (!data) return;
  try {
    for (let key in data) {
      peer[key] = data[key];
    }
  } catch (err) {
    console.error(err);
  }
}
function addStreamToPeer(connection, stream, name) {
  let peer = connection.pc;
  if (peer === void 0 || peer.destroyed) return;
  let oldStream = peer.streams[name];
  if (oldStream && oldStream === stream) return;
  if (oldStream) {
    try {
      peer.removeStream(oldStream);
    } catch (err) {
      console.warn(err);
    }
  }
  log3("adding stream to", s(peer.peerId), name);
  peer.streams[name] = stream;
  if (stream) {
    try {
      peer.addStream(stream);
    } catch (err) {
      peer.streams[name] = null;
      if (err.code) {
        throw err;
      } else {
        console.error("could not add stream to peer", peer.peerId);
        console.error(err);
      }
    }
  }
}
var s = id => id.slice(0, 2);
function log3() {
  if (!window.DEBUG) return;
  let d = new Date();
  let time = `[${d.toLocaleTimeString("de-DE")},${String(d.getMilliseconds()).padStart(3, "0")}]`;
  for (var _len9 = arguments.length, args = new Array(_len9), _key9 = 0; _key9 < _len9; _key9++) {
    args[_key9] = arguments[_key9];
  }
  log(time, ...args);
}

// ../../lib/swarm-state.js
import { emit as emit4, update as update3 } from "minimal-state";
function updatePeerState(_ref19, fullState) {
  let {
    swarm,
    peerId,
    connId
  } = _ref19;
  var _a4, _b;
  if (fullState === void 0) return;
  let {
    state,
    time
  } = fullState;
  let peerStates = get2(swarm.connectionState, peerId, {});
  let states = get2(peerStates, "states", {});
  states[connId] = {
    state: {
      ...((_b = (_a4 = states[connId]) == null ? void 0 : _a4.state) != null ? _b : null),
      ...state
    },
    time
  };
  reducePeerState(swarm, peerId);
  emit4(swarm, "rawPeerState", state, peerId, connId);
  update3(swarm, "connectionState");
  update3(swarm.connectionState, peerId);
  update3(swarm, "peerState");
  update3(swarm.peerState, peerId);
}
function removePeerState(_ref20) {
  let {
    swarm,
    peerId,
    connId
  } = _ref20;
  let peerStates = swarm.connectionState[peerId];
  if (peerStates === void 0) return;
  let {
    states
  } = peerStates;
  delete states[connId];
  if (Object.keys(states).length === 0) {
    delete swarm.connectionState[peerId];
    delete swarm.peerState[peerId];
  } else {
    reducePeerState(swarm, peerId);
  }
  update3(swarm, "connectionState");
  update3(swarm.connectionState, peerId);
  update3(swarm, "peerState");
  update3(swarm.peerState, peerId);
}
function reducePeerState(swarm, peerId) {
  let peerStates = swarm.connectionState[peerId];
  let {
    states
  } = peerStates;
  let {
    latestId,
    latestState
  } = findLatest(states);
  peerStates.latest = latestId;
  try {
    swarm.peerState[peerId] = swarm.reduceState(Object.values(states).map(s3 => s3.state), swarm.peerState[peerId], latestState, filter => {
      var _a4;
      return (_a4 = findLatest(states, filter)) == null ? void 0 : _a4.latestState;
    });
  } catch (err) {
    console.error(err);
  }
}
function findLatest(states, filter) {
  let latestId, latestTime, latestState;
  for (let connId in states) {
    let {
      state,
      time
    } = states[connId];
    if ((latestTime === void 0 || time > latestTime) && (filter === void 0 || filter(state))) {
      latestTime = time;
      latestState = state;
      latestId = connId;
    }
  }
  return {
    latestId,
    latestState
  };
}
function get2(object, key, init) {
  let value = object[key];
  if (value === void 0) {
    value = init;
    object[key] = init;
  }
  return value;
}

// ../../lib/swarm.js
function Swarm(initialConfig) {
  const swarm = {
    peers: {},
    myPeer: {
      connections: {}
    },
    myPeerId: null,
    connected: false,
    connectionStreams: [],
    remoteStreams: [],
    peerState: {},
    connectionState: {},
    myPeerState: {},
    url: "",
    room: "",
    debug: false,
    autoConnect: true,
    hub: null,
    localStreams: {},
    reduceState: (_states, _current, latest) => latest,
    sharedStateTime: Date.now(),
    connectState: INITIAL,
    stream: null,
    data: null,
    newPeer: null,
    newConnection: null,
    failedConnection: null,
    rawPeerState: null,
    peerEvent: {},
    serverEvent: {}
  };
  swarm.config = function () {
    for (var _len10 = arguments.length, args = new Array(_len10), _key10 = 0; _key10 < _len10; _key10++) {
      args[_key10] = arguments[_key10];
    }
    return config(swarm, ...args);
  };
  swarm.connect = function () {
    for (var _len11 = arguments.length, args = new Array(_len11), _key11 = 0; _key11 < _len11; _key11++) {
      args[_key11] = arguments[_key11];
    }
    return connect(swarm, ...args);
  };
  swarm.disconnect = function () {
    for (var _len12 = arguments.length, args = new Array(_len12), _key12 = 0; _key12 < _len12; _key12++) {
      args[_key12] = arguments[_key12];
    }
    return disconnect(swarm, ...args);
  };
  if (initialConfig !== void 0) {
    config(swarm, initialConfig);
  }
  on6(swarm.myPeerState, () => {
    var _a4;
    let time = Date.now();
    swarm.sharedStateTime = time;
    (_a4 = swarm.hub) == null ? void 0 : _a4.broadcast("all", {
      type: "shared-state",
      data: {
        state: swarm.myPeerState,
        time
      }
    });
    update4(swarm, "myPeerState");
  });
  checkWsHealth(swarm);
  on6(swarm.serverEvent, "new-consumer", data => {
    log3("got new consumer", data);
  });
  return swarm;
}
function config(swarm, _ref21) {
  let {
    url,
    room,
    myPeerId,
    sign: sign2,
    verify: verify2,
    reduceState,
    pcConfig,
    debug: debug2,
    autoConnect
  } = _ref21;
  mergeObject(swarm, {
    url,
    room,
    myPeerId,
    sign: sign2,
    verify: verify2,
    reduceState,
    pcConfig,
    debug: debug2,
    autoConnect
  });
}
function addLocalStream(swarm, stream, name) {
  log3("addlocalstream", stream, name);
  if (!name) name = randomHex4();
  swarm.localStreams[name] = stream;
  try {
    for (let connection of yieldConnections(swarm)) {
      addStreamToPeer(connection, stream, name);
    }
  } catch (err) {
    console.error("ERROR: add stream to peers");
    console.error(err);
  }
}
function sendPeerEvent(swarm, event2, payload) {
  var _a4;
  (_a4 = swarm.hub) == null ? void 0 : _a4.broadcast("all", {
    type: "peer-event",
    data: {
      t: event2,
      d: payload
    }
  });
}
function sendEventToOnePeer(swarm, peerId, event2, payload) {
  var _a4;
  for (let connection of yieldConnectionsOfPeer(swarm, peerId)) {
    (_a4 = swarm.hub) == null ? void 0 : _a4.sendDirect(connection, {
      type: "peer-event",
      data: {
        t: event2,
        d: payload
      }
    });
  }
}
function shareStateWithGroup(swarm, topic, state) {
  var _a4;
  let time = Date.now();
  (_a4 = swarm.hub) == null ? void 0 : _a4.broadcast(topic, {
    type: "shared-state",
    data: {
      state,
      time
    }
  });
}
function shareStateWithPeer(swarm, peerId, state) {
  var _a4;
  let time = Date.now();
  for (let connection of yieldConnectionsOfPeer(swarm, peerId)) {
    (_a4 = swarm.hub) == null ? void 0 : _a4.sendDirect(connection, {
      type: "shared-state",
      data: {
        state,
        time
      }
    });
  }
}
async function connect(swarm, roomId, myPeerId) {
  if (!(roomId || swarm.room) || !(myPeerId || swarm.myPeerId) || !swarm.url) {
    return console.error("Must call swarm.config({url, room, myPeerId}) before connecting!");
  }
  switch (swarm.connectState) {
    case CONNECTED:
    case CONNECTING:
      if (roomId && roomId !== swarm.room || myPeerId && myPeerId !== swarm.myPeerId) {
        disconnect(swarm);
      } else {
        return;
      }
      break;
    case DISCONNECTED:
      if (swarm.hub) {
        clear3(swarm.hub);
        swarm.hub.close();
      }
      for (let connection of yieldConnections(swarm)) {
        removeConnection(connection);
      }
      break;
  }
  config(swarm, {
    room: roomId,
    myPeerId
  });
  myPeerId = swarm.myPeerId;
  let myConnId = randomHex4();
  swarm.myConnId = myConnId;
  log3("connecting. conn id", myConnId);
  swarm.connectState = CONNECTING;
  let {
    sign: sign2,
    verify: verify2
  } = swarm;
  let myCombinedPeerId = `${myPeerId}.${myConnId}`;
  let hub = await signalws({
    roomId: swarm.room,
    url: swarm.url,
    myPeerId,
    myConnId,
    sign: sign2,
    verify: verify2,
    subscriptions: ["all"]
  });
  if (swarm.myConnId !== myConnId) {
    hub.close();
    return;
  }
  on6(hub, "opened", () => {
    swarm.connectState = CONNECTED;
    is4(swarm, "connected", true);
  });
  on6(hub, "closed", () => {
    disconnectUnwanted(swarm);
  });
  on6(hub, "peers", peers_ => {
    for (let combinedPeerId of peers_) {
      if (combinedPeerId === myCombinedPeerId) continue;
      initializeConnection(swarm, combinedPeerId);
    }
  });
  on6(hub, "add-peer", combinedPeerId => {
    let connection = initializeConnection(swarm, combinedPeerId);
    hub.sendDirect(connection, {
      type: "shared-state",
      data: {
        state: swarm.myPeerState,
        time: swarm.sharedStateTime
      }
    });
  });
  on6(hub, "remove-peer", id => {
    let [peerId, connId] = id.split(".");
    if (getPeer(swarm, peerId) === void 0) return;
    removeConnection({
      swarm,
      peerId,
      connId
    });
  });
  hub.broadcast("all", {
    type: "shared-state",
    data: {
      state: swarm.myPeerState,
      time: swarm.sharedStateTime
    }
  });
  swarm.hub = hub;
  on6(hub, "all", _ref22 => {
    let {
      type,
      peerId,
      connId,
      data
    } = _ref22;
    initializePeer(swarm, peerId);
    if (type === "shared-state") {
      let connection = getConnection(swarm, peerId, connId);
      updatePeerState(connection, data);
    }
    if (type === "peer-event") {
      emit5(swarm.peerEvent, data.t, peerId, data.d);
    }
  });
  on6(hub, "direct", _ref23 => {
    let {
      type,
      peerId,
      connId,
      data
    } = _ref23;
    initializePeer(swarm, peerId);
    if (type === "signal") {
      log3("signal received from", s2(peerId), connId, data.type);
      let connection = getConnection(swarm, peerId, connId);
      handleSignal(connection, {
        data
      });
    }
    if (type === "shared-state") {
      let connection = getConnection(swarm, peerId, connId);
      updatePeerState(connection, data);
    }
    if (type === "peer-event") {
      emit5(swarm.peerEvent, data.t, peerId, data.d);
    }
  });
  on6(hub, "server", (_ref24, accept) => {
    let {
      t: event2,
      d: payload
    } = _ref24;
    emit5(swarm.serverEvent, event2, payload, accept);
  });
  emit5(swarm, "hub-created");
}
function disconnect(swarm) {
  if (swarm.connectState === INITIAL) return;
  if (swarm.hub) swarm.hub.close();
  swarm.hub = null;
  swarm.myConnId = null;
  swarm.connectState = INITIAL;
  is4(swarm, "connected", false);
  for (let connection of yieldConnections(swarm)) {
    removeConnection(connection);
  }
}
function disconnectUnwanted(swarm) {
  if (swarm.connectState === INITIAL) return;
  swarm.connectState = DISCONNECTED;
  is4(swarm, "connected", false);
}
function initializePeer(swarm, peerId) {
  if (peerId === swarm.myPeerId) return;
  if (swarm.peers[peerId] === void 0) {
    swarm.peers[peerId] = {
      connections: {}
    };
    update4(swarm, "peers");
    emit5(swarm, "newPeer", peerId);
  }
}
function initializeConnection(swarm, combinedPeerId) {
  let [peerId, connId] = combinedPeerId.split(".");
  initializePeer(swarm, peerId);
  let connection = getConnection(swarm, peerId, connId);
  emit5(swarm, "newConnection", connection);
  if (swarm.autoConnect) connectPeer(connection);
  return connection;
}
function getPeer(swarm, peerId) {
  return peerId === swarm.myPeerId ? swarm.myPeer : swarm.peers[peerId];
}
function getConnection(swarm, peerId, connId) {
  let peer = getPeer(swarm, peerId);
  if (peer === void 0) return;
  let connection = peer.connections[connId];
  if (connection === void 0) {
    connection = newConnection({
      swarm,
      peerId,
      connId
    });
    peer.connections[connId] = connection;
    update4(swarm, "peers");
  }
  return connection;
}
function removeConnection(_ref25) {
  let {
    swarm,
    peerId,
    connId
  } = _ref25;
  log3("removing peer", s2(peerId), connId);
  let peer = getPeer(swarm, peerId);
  if (peer !== void 0) {
    let connection = peer.connections[connId];
    if (connection !== void 0) {
      disconnectPeer(connection);
    }
    delete peer.connections[connId];
  }
  let nConnections = Object.keys((peer == null ? void 0 : peer.connections) || {}).length;
  if (nConnections === 0 && peerId !== swarm.myPeerId) {
    delete swarm.peers[peerId];
  }
  update4(swarm, "peers");
  removePeerState({
    swarm,
    peerId,
    connId
  });
}
function* yieldConnections(swarm) {
  let {
    peers,
    myPeer
  } = swarm;
  for (let connId in myPeer.connections) {
    yield myPeer.connections[connId];
  }
  for (let peerId in peers) {
    let {
      connections
    } = peers[peerId];
    for (let connId in connections) {
      yield connections[connId];
    }
  }
}
function* yieldConnectionsOfPeer(swarm, peerId) {
  let peer = getPeer(swarm, peerId);
  if (peer === void 0) return;
  let {
    connections
  } = peer;
  for (let connId in connections) {
    yield connections[connId];
  }
}
function randomHex4() {
  return (Math.random() * 16 ** 4 | 0).toString(16).padStart(4, "0");
}
var s2 = id => id.slice(0, 2);

// ../../jam-core/identity.ts
var onload = domEvent(window, "load");
var identities = StoredState("identities");
var identityReady = (async () => {
  if (identities._default) return;
  let identity = importLegacyIdentity() || (await createIdentity());
  set5(identities, "_default", identity);
  migrateDisplayName(identities);
})();
function Identity(_ref26) {
  let {
    swarm
  } = _ref26;
  const hasPosted = new Map();
  Promise.all([onload, identityReady]).then(() => {
    setTimeout(() => {
      maybePostIdentity("_default", identities._default);
    }, 0);
  });
  function maybePostIdentity(roomId, identity) {
    let previous = hasPosted.get(roomId);
    if (previous && previous === identity.info) return;
    hasPosted.set(roomId, identity.info);
    if (!previous) {
      putOrPost({
        myIdentity: identity
      }, `/identities/${identity.publicKey}`, identity.info);
    }
    if (identity.publicKey === swarm.myPeerId) {
      sendPeerEvent(swarm, "identity-update", identity.info);
    }
  }
  return function Identity2(_ref27) {
    let {
      roomId
    } = _ref27;
    let defaultIdentity = use(identities, "_default");
    let roomIdentity = use(identities, roomId);
    if (roomIdentity !== void 0) {
      maybePostIdentity(roomId, roomIdentity);
    }
    let myIdentity = roomIdentity != null ? roomIdentity : defaultIdentity;
    return myIdentity;
  };
}
async function updateInfo(state, info) {
  let {
    myIdentity,
    myId,
    swarm
  } = state;
  info = {
    ...myIdentity.info,
    ...info
  };
  let ok = await putOrPost(state, `/identities/${myId}`, info);
  if (ok) {
    setCurrentIdentity(state, i => ({
      ...i,
      info
    }));
    sendPeerEvent(swarm, "identity-update", info);
  }
  return ok;
}
function setCurrentIdentity(_ref28, valueOrFunction) {
  let {
    roomId
  } = _ref28;
  let identityKey = identities[roomId] ? roomId : "_default";
  set5(identities, identityKey, valueOrFunction);
}
async function importDefaultIdentity(identity) {
  await importRoomIdentity("_default", identity);
}
async function importRoomIdentity(roomId, identity) {
  let fullIdentity;
  let existingIdentity = identities[roomId];
  if (identity.secretKey && identity.publicKey) {
    fullIdentity = {
      publicKey: identity.publicKey,
      secretKey: identity.secretKey,
      info: {
        ...identity.info,
        id: identity.publicKey
      }
    };
  } else if (identity.secretKey) {
    fullIdentity = await createIdentityFromSecretKey(identity.info, identity.secretKey);
  } else if (identity.seed) {
    fullIdentity = await createIdentityFromSeed(identity.info, identity.seed);
  } else if (existingIdentity) {
    fullIdentity = existingIdentity;
    fullIdentity.info = {
      ...existingIdentity.info,
      ...identity.info,
      id: existingIdentity.publicKey
    };
  } else {
    fullIdentity = await createIdentity(identity.info);
  }
  set5(identities, roomId, fullIdentity);
  let ok = await putOrPost({
    myIdentity: fullIdentity
  }, `/identities/${fullIdentity.publicKey}`, fullIdentity.info);
  if (!ok) {
    console.error("importing identity failed!");
  }
}
async function createIdentityFromSecretKey(info, privatekeyBase64) {
  const keypair2 = await keyPairFromSecretKey(decode(privatekeyBase64));
  return createIdentityFromKeypair(info, keypair2);
}
async function createIdentityFromSeed(info, seedString) {
  const hash = new Uint8Array(await crypto.subtle.digest("SHA-512", new TextEncoder().encode(seedString))).slice(0, 32);
  const keypair2 = await keyPairFromSeed(hash);
  return createIdentityFromKeypair(info, keypair2);
}
async function createIdentity(info) {
  const keypair2 = await newKeyPair();
  return createIdentityFromKeypair(info, keypair2);
}
function createIdentityFromKeypair(info, keypair2) {
  let publicKey = encode(keypair2.publicKey);
  let secretKey = encode(keypair2.secretKey);
  return {
    publicKey,
    secretKey,
    info: {
      ...info,
      id: publicKey
    }
  };
}

// ../../jam-core/admin.js
var addAdmin = async (state, id) => {
  return await post(state, `/admin/${id}`, {});
};
var removeAdmin = async (state, id) => {
  return await deleteRequest(state, `/admin/${id}`, {});
};

// ../../jam-core/AppState.js
import { is as is11 } from "minimal-state";

// ../../jam-core/audio/Microphone.js
import { is as is5 } from "minimal-state";

// ../../lib/user-agent.js
import UAParser from "ua-parser-js";
var userAgent = UAParser({
  browser: [[/(jamwebview)\/(ios|android)/i], [UAParser.BROWSER.NAME, UAParser.OS.NAME]]
});

// ../../jam-core/audio/Microphone.js
function Microphone() {
  const selectedMicrophoneState = StoredState("jam.selectedMicrophone");
  let availableMicrophones = [];
  let micState = "initial";
  let micStream = null;
  let hasRequestedOnce = false;
  const update8 = useUpdate();
  useUnmount(() => {
    micStream == null ? void 0 : micStream.getTracks().forEach(track => track.stop());
  });
  async function requestMic() {
    try {
      const constraints = !!selectedMicrophoneState.deviceId ? {
        audio: {
          deviceId: {
            exact: selectedMicrophoneState.deviceId
          }
        }
      } : {
        video: false,
        audio: true
      };
      let stream = await navigator.mediaDevices.getUserMedia(constraints);
      availableMicrophones = (await navigator.mediaDevices.enumerateDevices()).filter(d => d.kind === "audioinput");
      hasRequestedOnce = true;
      if (micState !== "requesting") return;
      micState = "active";
      micStream = stream;
    } catch (err) {
      if (micState !== "requesting") return;
      console.error("error getting mic", err);
      micState = "failed";
      micStream = null;
    }
    update8();
  }
  function forceRetryMic() {
    var _a4;
    if (((_a4 = userAgent.browser) == null ? void 0 : _a4.name) === "Safari") {
      location.reload();
    } else {
      micState = "requesting";
      requestMic();
    }
  }
  return function Microphone2(_ref29) {
    let {
      shouldHaveMic = true
    } = _ref29;
    let [isRetry] = useAction(actions.RETRY_MIC);
    let [isSelectMicrophone, selectedMicrophone] = useAction(actions.SELECT_MIC);
    if (isSelectMicrophone) {
      is5(selectedMicrophoneState, "deviceId", selectedMicrophone.deviceId);
    }
    switch (micState) {
      case "initial":
        if (shouldHaveMic) {
          micState = "requesting";
          requestMic();
        }
        break;
      case "requesting":
        if (!shouldHaveMic) {
          micState = "initial";
        }
        break;
      case "active":
        if (!shouldHaveMic) {
          micStream.getTracks().forEach(track => track.stop());
          micStream = null;
          micState = "initial";
        } else if (isSelectMicrophone) {
          micStream.getTracks().forEach(track => track.stop());
          micStream = null;
          micState = "requesting";
          requestMic();
        } else if (isRetry && !micStream.active) {
          forceRetryMic();
        }
        break;
      case "failed":
        if (!shouldHaveMic) {
          micState = "initial";
        } else if (isRetry) {
          forceRetryMic();
        }
        break;
    }
    return {
      micStream,
      hasRequestedOnce,
      hasMicFailed: micState === "failed",
      availableMicrophones,
      selectedMicrophoneId: selectedMicrophoneState.deviceId
    };
  };
}

// ../../jam-core/audio/AudioFile.js
import { is as is6 } from "minimal-state";
function AudioFile() {
  let audioState = "initial";
  let activeFile = null;
  let audio = null;
  let stream = null;
  const update8 = useUpdate();
  const state = useRootState();
  async function createAudioFileStream(file, ctx) {
    let url = URL.createObjectURL(file);
    audio = new Audio(url);
    audio.crossOrigin = "anonymous";
    let streamDestination = ctx.createMediaStreamDestination();
    let source = ctx.createMediaElementSource(audio);
    source.connect(streamDestination);
    source.connect(ctx.destination);
    stream = streamDestination.stream;
    await audio.play();
    audioState = "playing";
    update8();
    domEvent(audio, "ended").then(() => {
      if (activeFile === file) {
        closeAudioFileStream();
      }
    });
  }
  function closeAudioFileStream() {
    audioState = "initial";
    if (audio) {
      let url = audio.src;
      audio.removeAttribute("src");
      if (url) URL.revokeObjectURL(url);
    }
    audio = null;
    stream = null;
    activeFile = null;
    is6(state, "audioFile", null);
  }
  return function AudioFile2(_ref30) {
    let {
      audioContext
    } = _ref30;
    let audioFile = useRootState("audioFile");
    let {
      file
    } = audioFile != null ? audioFile : {};
    let shouldPlay = file && audioContext;
    switch (audioState) {
      case "initial":
        if (shouldPlay) {
          audioState = "loading";
          activeFile = file;
          createAudioFileStream(file, audioContext);
        }
        break;
      case "loading":
        break;
      case "playing":
        if (!shouldPlay) {
          audioState = "initial";
          closeAudioFileStream();
        } else if (file !== activeFile) {
          audioState = "loading";
          activeFile = file;
          createAudioFileStream(file, audioContext);
        }
        break;
    }
    return {
      audioFileStream: stream,
      audioFileElement: audio
    };
  };
}

// ../../jam-core/audio/PlayingAudio.js
import { is as is7 } from "minimal-state";

// ../../jam-core/audio/VolumeMeter.js
import { on as on7, update as update5, clear as clear4, emit as emit6 } from "minimal-state";
function VolumeMeter(_ref31) {
  let {
    injectedState
  } = _ref31;
  const state = injectedState || useRootState();
  let volumeMeter = null;
  let activeStream = null;
  useUnmount(() => {
    if (volumeMeter !== null) volumeMeter.stop();
  });
  return function VolumeMeter2(_ref32) {
    let {
      stream,
      audioContext,
      peerId
    } = _ref32;
    if (stream && audioContext && stream !== activeStream) {
      activeStream = stream;
      if (volumeMeter !== null) volumeMeter.stop();
      volumeMeter = startVolumeMeter(stream, audioContext);
      on7(volumeMeter, "speaking", () => {
        state.speaking.add(peerId);
        update5(state, "speaking");
      });
      on7(volumeMeter, "stopped_speaking", () => {
        state.speaking.delete(peerId);
        update5(state, "speaking");
      });
    } else if (!stream && activeStream) {
      activeStream = null;
      volumeMeter.stop();
      volumeMeter = null;
    }
  };
}
var SMOOTHING = 0.1;
var INTERVAL = 50;
var THRESHOLD = -50;
var HISTORY_LENGTH = 10;
function startVolumeMeter(stream, audioContext) {
  if (!audioContext) return {};
  let analyser = audioContext.createAnalyser();
  analyser.fftSize = 512;
  analyser.smoothingTimeConstant = SMOOTHING;
  let fftBins = new Float32Array(analyser.frequencyBinCount);
  let sourceNode = audioContext.createMediaStreamSource(stream);
  sourceNode.connect(analyser);
  let speakingHistory = [];
  for (let i = 0; i < HISTORY_LENGTH; i++) {
    speakingHistory.push(0);
  }
  const volumeMeter = {
    running: true,
    speaking: false,
    threshold: THRESHOLD,
    stop() {
      volumeMeter.running = false;
      if (volumeMeter.speaking) {
        volumeMeter.speaking = false;
        emit6(volumeMeter, "stopped_speaking");
      }
      analyser.disconnect();
      sourceNode.disconnect();
      clear4(volumeMeter);
    },
    speakingHistory,
    analyser,
    fftBins
  };
  pollAnalyser(volumeMeter);
  return volumeMeter;
}
function pollAnalyser(volumeMeter) {
  setTimeout(() => {
    if (!volumeMeter.running) return;
    let {
      threshold,
      analyser,
      fftBins,
      speaking,
      speakingHistory
    } = volumeMeter;
    let currentVolume = getMaxVolume(analyser, fftBins);
    let historyCount = 0;
    if (currentVolume > threshold && !speaking) {
      for (let i = speakingHistory.length - 3; i < speakingHistory.length; i++) {
        historyCount += speakingHistory[i];
      }
      if (historyCount >= 2) {
        volumeMeter.speaking = true;
        emit6(volumeMeter, "speaking");
      }
    } else if (currentVolume < threshold && speaking) {
      for (let i = 0; i < speakingHistory.length; i++) {
        historyCount += speakingHistory[i];
      }
      if (historyCount === 0) {
        volumeMeter.speaking = false;
        emit6(volumeMeter, "stopped_speaking");
      }
    }
    speakingHistory.shift();
    speakingHistory.push(0 + (currentVolume > threshold));
    pollAnalyser(volumeMeter);
  }, INTERVAL);
}
function getMaxVolume(analyser, fftBins) {
  var maxVolume = -Infinity;
  analyser.getFloatFrequencyData(fftBins);
  for (let i = 4, ii = fftBins.length; i < ii; i++) {
    if (fftBins[i] > maxVolume && fftBins[i] < 0) {
      maxVolume = fftBins[i];
    }
  }
  return maxVolume;
}

// ../../jam-core/audio/PlayingAudio.js
function PlayingAudio(_ref33) {
  let {
    audioElements,
    peerId,
    soundMuted
  } = _ref33;
  const state = useRootState();
  let activeStream = null;
  let clonedStream = null;
  let audio = audioElements[peerId];
  if (!audio) {
    audio = new Audio();
    audioElements[peerId] = audio;
    audio.muted = soundMuted;
  }
  return function PlayingAudio2(_ref34) {
    let {
      stream,
      audioContext,
      soundMuted: soundMuted2,
      shouldPlay
    } = _ref34;
    let shouldStartPlaying = false;
    if (activeStream !== stream && stream) {
      activeStream = stream;
      clonedStream = stream.clone();
      audio.removeAttribute("srcObject");
      audio.load();
      audio.srcObject = stream;
      if (shouldPlay) {
        shouldStartPlaying = true;
      }
    }
    if (useDidChange(soundMuted2)) {
      audio.muted = soundMuted2;
      if (!soundMuted2 && audio.paused) {
        shouldStartPlaying = true;
      }
    }
    if (shouldStartPlaying) {
      play(audio).catch(err => {
        if (stream === activeStream) {
          console.warn(err);
          if (shouldPlay) is7(state, "audioPlayError", true);
        } else {}
      });
    }
    let [isRetrySound] = useAction(actions.RETRY_AUDIO);
    if (isRetrySound) {
      if (audio.paused) play(audio).catch(console.warn);
    }
    declare(VolumeMeter, {
      peerId,
      stream: clonedStream,
      audioContext
    });
  };
}
function play(audio) {
  log("playing audio on engine", userAgent.engine.name);
  if (userAgent.engine.name === "WebKit") {
    return audio.play().then(() => {
      let onpause = domEvent(audio, "pause");
      audio.pause();
      return onpause;
    }).then(() => audio.play());
  } else {
    return audio.play();
  }
}

// ../../jam-core/audio.js
import { is as is9 } from "minimal-state";

// ../../jam-core/audio/Recording.js
import { is as is8 } from "minimal-state";
function Recording(_ref35) {
  let {
    swarm
  } = _ref35;
  let recordingDestination = null;
  let recordedAudio = null;
  let isRecording = false;
  let mediaRecorder = null;
  let recordedChunks = null;
  let shouldDownload = false;
  let downloadFileName;
  let update8 = useUpdate();
  return function Recording2(_ref36) {
    let {
      audioContext,
      myAudio,
      remoteStreams
    } = _ref36;
    let [isStart] = useAction("start-recording");
    let [isStop] = useAction("stop-recording");
    let [isDownload, fileName] = useAction("download-recording");
    if (isStart) {
      if (mediaRecorder !== null) {
        mediaRecorder.stop();
      }
      if (recordingDestination === null) {
        recordingDestination = audioContext.createMediaStreamDestination();
      }
      recordedChunks = [];
      recordedAudio = null;
      mediaRecorder = new MediaRecorder(recordingDestination.stream);
      isRecording = true;
      console.log("started media recorder", mediaRecorder);
      mediaRecorder.ondataavailable = event2 => {
        if (event2.data.size > 0) {
          recordedChunks.push(event2.data);
        }
      };
      mediaRecorder.onstop = () => {
        recordedAudio = new Blob(recordedChunks != null ? recordedChunks : [], {
          type: "audio/mp3; codecs=opus"
        });
        update8();
      };
      mediaRecorder.start();
    }
    if (isStop) {
      mediaRecorder == null ? void 0 : mediaRecorder.stop();
      mediaRecorder = null;
      isRecording = false;
    }
    is8(swarm.myPeerState, {
      isRecording
    });
    let isSomeoneRecording = use(swarm.peerState, state => Object.values(state != null ? state : {}).some(s3 => s3 == null ? void 0 : s3.isRecording)) || isRecording;
    declare(RecordStream, {
      key: myAudio,
      stream: myAudio,
      audioContext,
      isRecording,
      recordingDestination
    });
    for (let s3 of remoteStreams) {
      let {
        stream
      } = s3;
      declare(RecordStream, {
        key: stream,
        stream,
        audioContext,
        isRecording,
        recordingDestination
      });
    }
    if (isDownload) {
      shouldDownload = true;
      downloadFileName = fileName;
    }
    if (shouldDownload && (recordedAudio == null ? void 0 : recordedAudio.size) > 0) {
      shouldDownload = false;
      downloadMp3(downloadFileName, recordedAudio);
      downloadFileName = void 0;
    }
    return {
      recordedAudio,
      isRecording,
      isSomeoneRecording
    };
  };
}
function RecordStream(_ref37) {
  let {
    stream,
    audioContext,
    isRecording,
    recordingDestination
  } = _ref37;
  let [sourceNode, setSourceNode] = useState(null);
  if (isRecording && sourceNode === null) {
    sourceNode = audioContext.createMediaStreamSource(stream);
    sourceNode.connect(recordingDestination);
    setSourceNode(sourceNode);
  }
  useUnmount(() => {
    sourceNode == null ? void 0 : sourceNode.disconnect();
  });
}
function downloadMp3(fileName, blob) {
  let url = URL.createObjectURL(blob);
  let a = document.createElement("a");
  document.body.appendChild(a);
  a.style = "display: none";
  a.href = url;
  a.download = `${fileName != null ? fileName : "recording"}.mp3`;
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ../../jam-core/audio/PodcastRecording.js
import { toBytes as toBytes3, toBase64 as toBase643 } from "fast-base64";
function PodcastRecording(_ref38) {
  let {
    swarm
  } = _ref38;
  let localRecordings = {};
  let isPodcasting = false;
  let podcastChunks = {};
  let podcastTracks = {};
  let update8 = useUpdate();
  useOn(swarm.peerEvent, "podcast-chunk", async (peerId, chunk) => {
    if (!(peerId in podcastChunks)) podcastChunks[peerId] = [];
    chunk = await toBytes3(chunk);
    podcastChunks[peerId].push(chunk);
  });
  useOn(swarm.peerEvent, "podcast-chunk-end", peerId => {
    if (!(peerId in podcastChunks)) return;
    podcastTracks[peerId] = new Blob(podcastChunks[peerId], {
      type: "audio/opus; codecs=opus"
    });
    delete podcastChunks[peerId];
    downloadMp32(`podcast-track-${peerId}`, podcastTracks[peerId]);
    update8();
  });
  return function PodcastRecording2(_ref39) {
    let {
      audioContext,
      myAudio,
      iAmSpeaker,
      iAmModerator,
      moderators
    } = _ref39;
    var _a4, _b;
    let [isPodcasterEvent, podcasterId, type] = useEvent(swarm.peerEvent, "podcaster");
    if (isPodcasterEvent) {
      let isModerator = moderators == null ? void 0 : moderators.includes(podcasterId);
      if (type === "start" && isModerator) {
        localRecordings[podcasterId] = (_a4 = localRecordings[podcasterId]) != null ? _a4 : {};
        localRecordings[podcasterId].state = "start";
      } else if (type === "stop") {
        localRecordings[podcasterId] = (_b = localRecordings[podcasterId]) != null ? _b : {};
        localRecordings[podcasterId].state = "stop";
      }
    }
    let [isPodcastEnded, endedPodcasterId] = useAction("podcast-ended");
    if (isPodcastEnded) {
      delete localRecordings[endedPodcasterId];
    }
    let isSomeonePodcasting = Object.values(localRecordings).some(s3 => s3.state === "start");
    for (let peerId in localRecordings) {
      let recordingState = localRecordings[peerId].state;
      declare(LocalRecording, {
        key: peerId,
        podcasterId: peerId,
        swarm,
        myAudio,
        iAmSpeaker,
        recordingState,
        audioContext
      });
    }
    let [isStart] = useAction("start-podcast-recording");
    let [isStop] = useAction("stop-podcast-recording");
    isStart = iAmModerator && isStart;
    isStop = isStop || isPodcasting && !iAmModerator;
    if (isStart && !isStop && !isPodcasting) {
      isPodcasting = true;
      sendPeerEvent(swarm, "podcaster", "start");
    } else if (isStop && isPodcasting) {
      isPodcasting = false;
      sendPeerEvent(swarm, "podcaster", "stop");
    }
    return {
      podcastTracks,
      isPodcasting,
      isSomeonePodcasting
    };
  };
}
function LocalRecording(_ref40) {
  let {
    swarm,
    podcasterId
  } = _ref40;
  let isRecording = false;
  let mediaRecorder = null;
  let recordingDestination = null;
  let dispatch = useDispatch();
  return function LocalRecording2(_ref41) {
    let {
      myAudio,
      iAmSpeaker,
      recordingState,
      audioContext
    } = _ref41;
    let shouldRecord = recordingState === "start" && iAmSpeaker;
    let isStart = shouldRecord && !isRecording;
    let isStop = !shouldRecord && isRecording;
    if (isStart) {
      if (mediaRecorder !== null) {
        mediaRecorder.stop();
      }
      if (recordingDestination === null) {
        recordingDestination = audioContext.createMediaStreamDestination();
      }
      mediaRecorder = new MediaRecorder(recordingDestination.stream);
      isRecording = true;
      console.log("started media recorder", mediaRecorder);
      mediaRecorder.ondataavailable = async event2 => {
        if (event2.data.size > 0) {
          let chunk = await toBase643(new Uint8Array(await event2.data.arrayBuffer()));
          sendEventToOnePeer(swarm, podcasterId, "podcast-chunk", chunk);
        }
      };
      mediaRecorder.onstop = () => {
        setTimeout(() => {
          sendEventToOnePeer(swarm, podcasterId, "podcast-chunk-end");
          dispatch("podcast-ended", podcasterId);
        }, 100);
      };
      mediaRecorder.start(2e3);
    }
    if (isStop) {
      mediaRecorder == null ? void 0 : mediaRecorder.stop();
      mediaRecorder = null;
      isRecording = false;
    }
    declare(RecordStream2, {
      key: myAudio,
      stream: myAudio,
      audioContext,
      isRecording,
      recordingDestination
    });
  };
}
function RecordStream2(_ref42) {
  let {
    stream,
    audioContext,
    isRecording,
    recordingDestination
  } = _ref42;
  let [sourceNode, setSourceNode] = useState(null);
  if (isRecording && sourceNode === null) {
    sourceNode = audioContext.createMediaStreamSource(stream);
    sourceNode.connect(recordingDestination);
    setSourceNode(sourceNode);
  }
  useUnmount(() => {
    sourceNode == null ? void 0 : sourceNode.disconnect();
  });
}
function downloadMp32(fileName, blob) {
  let url = URL.createObjectURL(blob);
  let a = document.createElement("a");
  document.body.appendChild(a);
  a.style = "display: none";
  a.href = url;
  a.download = `${fileName != null ? fileName : "recording"}.opus`;
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ../../jam-core/audio.js
var AudioContext = window.AudioContext || window.webkitAudioContext;
function AudioState(_ref43) {
  let {
    swarm
  } = _ref43;
  const state = useRootState();
  const audioElements = {};
  let audioContext = null;
  let audioContextStarted = false;
  useOn(swarm, "newPeer", peerId => {
    if (!audioElements[peerId]) {
      audioElements[peerId] = new Audio();
      audioElements[peerId].muted = state.soundMuted;
    }
  });
  return function AudioState2(_ref44) {
    let {
      myId,
      inRoom,
      iAmSpeaker,
      iAmModerator,
      moderators,
      userInteracted,
      micMuted,
      handRaised,
      remoteStreams,
      broadcastPlayers,
      customStream
    } = _ref44;
    var _a4;
    if (audioContext === null && AudioContext) {
      let shouldHaveAudioContext = userInteracted || inRoom && (iAmSpeaker || remoteStreams.length > 0);
      if (shouldHaveAudioContext) {
        audioContext = new AudioContext();
        let now = Date.now();
        let onAudioContextState = () => {
          if (audioContext.state === "running") {
            audioContextStarted = true;
            console.warn("audioContext started after", Date.now() - now);
          }
        };
        audioContext.addEventListener("statechange", onAudioContextState);
        setTimeout(() => {
          audioContext.removeEventListener("statechange", onAudioContextState);
          if (audioContext.state === "running") {
            audioContextStarted = true;
          }
          if (!audioContextStarted) {
            is9(state, "audioPlayError", true);
          }
        }, 1e3);
      }
    }
    let [isRetrySound] = useAction(actions.RETRY_AUDIO);
    if (isRetrySound && audioContext && !audioContextStarted) {
      audioContext.resume();
    }
    let shouldHaveMic = !!(inRoom && (iAmSpeaker || handRaised));
    let {
      micStream,
      hasRequestedOnce,
      hasMicFailed,
      availableMicrophones,
      selectedMicrophoneId
    } = use(Microphone, {
      shouldHaveMic
    });
    let {
      audioFileStream,
      audioFileElement
    } = use(AudioFile, {
      audioContext
    });
    let myAudio = (_a4 = customStream != null ? customStream : audioFileStream) != null ? _a4 : micStream;
    declare(Muted, {
      myAudio,
      micMuted
    });
    if (iAmSpeaker) {
      declare(VolumeMeter, {
        peerId: myId,
        stream: myAudio,
        audioContext
      });
    }
    let soundMuted = !inRoom || iAmSpeaker && !hasRequestedOnce;
    remoteStreams.map(remoteStream => {
      let {
        peerId,
        stream,
        name
      } = remoteStream;
      if (name === "audio") return declare(PlayingAudio, {
        key: peerId,
        peerId,
        audioContext,
        audioElements,
        stream,
        soundMuted,
        shouldPlay: !!inRoom
      });
    });
    broadcastPlayers.forEach((player, playerId) => {
      const [source, peerId] = playerId.split("|");
      player.getMediaElement().play();
      player.goToLive();
      const injectedState = useRootState();
      setTimeout(() => {
        const stream = player.getMediaElement().captureStream();
        declare(VolumeMeter, {
          injectedState,
          peerId,
          stream,
          audioContext
        });
      }, 50);
    });
    return merge({
      myAudio,
      soundMuted,
      audioFileElement,
      hasMicFailed,
      availableMicrophones,
      selectedMicrophoneId,
      remoteAudioStreams: remoteStreams.filter(stream => stream.name === "audio")
    }, declare(Recording, {
      swarm,
      audioContext,
      myAudio,
      remoteStreams
    }), declare(PodcastRecording, {
      swarm,
      audioContext,
      myAudio,
      iAmSpeaker,
      iAmModerator,
      moderators
    }));
  };
}
function Muted(_ref45) {
  let {
    myAudio,
    micMuted
  } = _ref45;
  if (myAudio) {
    for (let track of myAudio.getTracks()) {
      if (track.enabled !== !micMuted) {
        track.enabled = !micMuted;
      }
    }
  }
}

// ../../jam-core/video/Camera.js
var videoQualityOptions = {
  LOW: "low",
  MEDIUM: "medium",
  HIGH: "high"
};
var qualityConstraints = videoQualityOption => {
  switch (videoQualityOption) {
    case videoQualityOptions.LOW:
      return {
        width: {
          ideal: 240
        },
        height: {
          ideal: 240
        }
      };
    case videoQualityOptions.MEDIUM:
      return {
        width: {
          ideal: 480
        },
        height: {
          ideal: 480
        }
      };
    default:
      return {};
  }
};
function Camera() {
  let camState = "initial";
  let camStream = null;
  let hasRequestedOnce = false;
  let usedCameraIds = new Set();
  let cameraOn = true;
  let videoQuality = videoQualityOptions.LOW;
  const update8 = useUpdate();
  useUnmount(() => {
    camStream == null ? void 0 : camStream.getTracks().forEach(track => track.stop());
  });
  async function requestCam() {
    var _a4;
    const permissionsStream = await navigator.mediaDevices.getUserMedia({
      video: true
    });
    permissionsStream == null ? void 0 : permissionsStream.getTracks().forEach(track => track.stop());
    try {
      const availableCameraIds = (await navigator.mediaDevices.enumerateDevices()).filter(d => d.kind === "videoinput").map(d => d.deviceId);
      if (availableCameraIds.length === 0) {
        console.log("no cameras available");
        camState = "failed";
        camStream = null;
        return;
      }
      let newCameraId = availableCameraIds[0];
      usedCameraIds.add(newCameraId);
      camStream == null ? void 0 : camStream.getTracks().forEach(track => track.stop());
      camState = "active";
      camStream = await navigator.mediaDevices.getUserMedia({
        video: {
          ...qualityConstraints(videoQuality),
          deviceId: {
            exact: newCameraId
          }
        }
      });
      const settings = (_a4 = camStream.getVideoTracks()[0]) == null ? void 0 : _a4.getSettings();
      if (settings) {
        console.log(`Resolution: ${settings.width}x${settings.height}`);
      }
    } catch (err) {
      console.error("error getting cam", err);
      camState = "failed";
      camStream = null;
    }
    await update8();
  }
  function forceRetryCam() {
    var _a4;
    if (((_a4 = userAgent.browser) == null ? void 0 : _a4.name) === "Safari") {
      location.reload();
    } else {
      camState = "requesting";
      switchCamera();
    }
  }
  async function switchCamera() {
    try {
      const availableCameraIds = (await navigator.mediaDevices.enumerateDevices()).filter(d => d.kind === "videoinput").map(d => d.deviceId);
      if (availableCameraIds.length === 0) {
        console.log("no cameras available");
        camState = "failed";
        camStream = null;
        return;
      }
      let newCameraId = availableCameraIds.find(id => !usedCameraIds.has(id));
      if (newCameraId === void 0) {
        usedCameraIds.clear();
        newCameraId = availableCameraIds[0];
      }
      usedCameraIds.add(newCameraId);
      camStream == null ? void 0 : camStream.getTracks().forEach(track => track.stop());
      camState = "active";
      camStream = await navigator.mediaDevices.getUserMedia({
        video: {
          deviceId: {
            exact: newCameraId
          }
        }
      });
    } catch (err) {
      console.error("error getting cam", err);
      camState = "failed";
      camStream = null;
    }
    update8();
  }
  return function Camera2(_ref46) {
    let {
      shouldHaveCam = true
    } = _ref46;
    let [isRetry] = useAction(actions.RETRY_CAM);
    let [isSwitchCam] = useAction(actions.SWITCH_CAM);
    let [isSetCamOn, camOn] = useAction(actions.SET_CAM_ON);
    if (isSetCamOn) {
      cameraOn = camOn;
    }
    const shouldHaveCamAndCamOn = cameraOn && shouldHaveCam;
    switch (camState) {
      case "initial":
        if (shouldHaveCamAndCamOn) {
          camState = "requesting";
          requestCam();
        }
        break;
      case "requesting":
        if (!shouldHaveCamAndCamOn) {
          camState = "initial";
        }
        break;
      case "active":
        if (!shouldHaveCamAndCamOn) {
          camStream.getTracks().forEach(track => track.stop());
          camStream = null;
          camState = "initial";
        } else if (isRetry && !camStream.active) {
          forceRetryCam();
        } else if (isSwitchCam) {
          camState = "requesting";
          switchCamera();
        }
        break;
      case "failed":
        if (!shouldHaveCamAndCamOn) {
          camState = "initial";
        } else if (isRetry) {
          forceRetryCam();
        } else if (isSwitchCam) {
          camState = "requesting";
          switchCamera();
        }
        break;
    }
    return {
      cameraOn,
      camStream,
      hasRequestedOnce,
      hasCamFailed: camState === "failed"
    };
  };
}

// ../../jam-core/video/ScreenShare.js
function ScreenShare() {
  let screenState = "initial";
  let screenStream = null;
  const update8 = useUpdate();
  let sharingScreen = false;
  useUnmount(() => {
    screenStream == null ? void 0 : screenStream.getTracks().forEach(track => track.stop());
  });
  async function requestScreen() {
    try {
      screenStream = await navigator.mediaDevices.getDisplayMedia();
      screenState = "active";
      screenStream.getVideoTracks()[0].addEventListener("ended", async () => {
        screenStream = null;
        sharingScreen = false;
        screenState = "initial";
        await update8();
      });
    } catch (err) {
      console.error("error getting cam", err);
      screenState = "failed";
      screenStream = null;
    }
    await update8();
  }
  return function ScreenShare2(_ref47) {
    let {
      canShareScreen = true
    } = _ref47;
    let [startScreenShare] = useAction(actions.START_SCREEN_SHARE);
    let [stopScreenShare] = useAction(actions.STOP_SCREEN_SHARE);
    if (startScreenShare) {
      sharingScreen = true;
    }
    if (stopScreenShare) {
      sharingScreen = false;
    }
    const shouldShareScreen = canShareScreen && sharingScreen;
    switch (screenState) {
      case "initial":
        if (shouldShareScreen) {
          screenState = "requesting";
          requestScreen();
        }
        break;
      case "requesting":
        if (!shouldShareScreen) {
          screenState = "initial";
        }
        break;
      case "active":
        if (!shouldShareScreen) {
          screenStream.getTracks().forEach(track => track.stop());
          screenStream = null;
          screenState = "initial";
        }
        break;
      case "failed":
        if (!shouldShareScreen) {
          screenState = "initial";
        }
        break;
    }
    return {
      screenStream,
      hasScreenFailed: screenState === "failed"
    };
  };
}

// ../../jam-core/video.js
function VideoState() {
  return function VideoState2(_ref48) {
    let {
      inRoom,
      iAmPresenter,
      remoteStreams
    } = _ref48;
    let shouldHaveCam = !!(inRoom && iAmPresenter);
    let camResult = use(Camera, {
      shouldHaveCam
    });
    let screenResult = use(ScreenShare, {
      shouldHaveCam
    });
    let {
      cameraOn,
      camStream,
      hasCamFailed
    } = camResult;
    let {
      screenStream,
      hasScreenFailed
    } = screenResult;
    const remoteVideoStreams = remoteStreams.filter(stream => stream.name === "video");
    const remoteScreenStreams = remoteStreams.filter(stream => stream.name === "screen");
    return {
      cameraOn,
      myVideo: camStream,
      myScreen: screenStream,
      hasScreenFailed,
      hasCamFailed,
      remoteVideoStreams,
      remoteScreenStreams
    };
  };
}

// ../../jam-core/backchannel.js
function BackChannel(_ref49) {
  let {
    swarm,
    myId
  } = _ref49;
  let backChannels = new Map();
  let subscriptionIds = new Set();
  return function BackChannel2() {
    const [isSubscribe, subscription] = useAction(actions.BACKCHANNEL_SUBSCRIBE);
    const [isUnsubscribe, subscriptionId] = useAction(actions.BACKCHANNEL_UNSUBSCRIBE);
    const [isBroadcast, message] = useAction(actions.BACKCHANNEL_BROADCAST);
    const ensureBackChannelPresence = async roomId => {
      if (!backChannels.has(roomId)) {
        const {
          url,
          sign: sign2
        } = swarm;
        const myConnId = randomHex42();
        backChannels.set(roomId, await signalws({
          url,
          roomId,
          myPeerId: myId,
          myConnId,
          sign: sign2,
          subscriptions: ["all"]
        }));
      }
    };
    if (isSubscribe) {
      const {
        roomId,
        handler,
        topic,
        subscriptionId: subscriptionId2
      } = subscription;
      ensureBackChannelPresence(roomId).then(() => {
        if (!subscriptionIds.has(subscriptionId2)) {
          subscriptionIds.add(subscriptionId2);
          const fencedHandler = data => {
            if (subscriptionIds.has(subscriptionId2)) {
              handler(data);
            }
          };
          backChannels.get(roomId).subscribe(topic, fencedHandler);
        }
      });
    }
    if (isUnsubscribe) {
      subscriptionIds.delete(subscriptionId);
    }
    if (isBroadcast) {
      const {
        roomId,
        topic,
        data
      } = message;
      ensureBackChannelPresence(roomId).then(() => backChannels.get(roomId).broadcast(topic, {
        data
      }));
    }
    return {
      backChannels: [...backChannels.keys()]
    };
  };
}
function randomHex42() {
  return (Math.random() * 16 ** 4 | 0).toString(16).padStart(4, "0");
}

// ../../jam-core/reactions.js
import { update as update6 } from "minimal-state";
function Reactions(_ref50) {
  let {
    swarm
  } = _ref50;
  const state = useRootState();
  useOn(swarm.peerEvent, "reaction", (peerId, reaction) => {
    if (peerId === swarm.myPeerId) return;
    if (reaction) showReaction(reaction, peerId);
  });
  function showReaction(reaction, peerId) {
    let {
      reactions
    } = state;
    if (!reactions[peerId]) reactions[peerId] = [];
    let reactionObj = [reaction, Math.random()];
    reactions[peerId].push(reactionObj);
    update6(state, "reactions");
    setTimeout(() => {
      let i = reactions[peerId].indexOf(reactionObj);
      if (i !== -1) reactions[peerId].splice(i, 1);
      update6(state, "reactions");
    }, 5e3);
  }
  return function Reactions2() {
    let [isReaction, reaction] = useAction(actions.REACTION);
    if (isReaction) {
      sendPeerEvent(swarm, "reaction", reaction);
      showReaction(reaction, swarm.myPeerId);
    }
  };
}

// ../../jam-core/room/ModeratorState.js
function ModeratorState(_ref51) {
  let {
    swarm,
    moderators,
    handRaised
  } = _ref51;
  let [isRaiseHand, hasRaisedHand] = useDidEverChange(handRaised, false);
  let newModerators = event(NewModerators, {
    moderators,
    swarm
  });
  if (isRaiseHand) {
    shareStateWithGroup(swarm, "moderator", {
      handRaised
    });
  }
  if (hasRaisedHand && newModerators) {
    for (let peerId of newModerators) {
      shareStateWithPeer(swarm, peerId, {
        handRaised
      });
    }
  }
}
function NewModerators(_ref52) {
  let {
    swarm
  } = _ref52;
  let modPeers = new Set();
  return function NewModerators2(_ref53) {
    let {
      moderators
    } = _ref53;
    let peers = Object.keys(use(swarm, "peers"));
    let newModPeers;
    [modPeers, newModPeers] = newIntersection(peers, moderators, modPeers);
    if (newModPeers.size > 0) {
      return newModPeers;
    }
  };
}
function newIntersection(arrA, arrB, AnB) {
  let newAnB = new Set();
  let added = new Set();
  for (let x of arrA) {
    if (arrB.includes(x)) {
      newAnB.add(x);
      if (!AnB.has(x)) {
        added.add(x);
      }
    }
  }
  return [newAnB, added];
}

// ../../jam-core/connections/Mediasoup.js
import { Device } from "mediasoup-client";
var INITIAL2 = 0;
var LOADING = 1;
var READY = 2;
function Mediasoup(_ref54) {
  let {
    swarm
  } = _ref54;
  let update8 = useUpdate();
  const {
    serverEvent
  } = swarm;
  let remoteStreams = [];
  let mediasoupState = INITIAL2;
  let sendState = INITIAL2;
  let receiveState = INITIAL2;
  let connectedRoomId = null;
  let sendingAudioStream = null;
  let sendingVideoStream = null;
  let sendingScreenStream = null;
  let audioProducer = null;
  let videoProducer = null;
  let screenProducer = null;
  let canUseMediasoup = false;
  let routerRtpCapabilities = null;
  let mediasoupDevice;
  try {
    mediasoupDevice = new Device();
  } catch (err) {
    console.warn(err);
    mediasoupDevice = new Device({
      handlerName: "Chrome74"
    });
  }
  let canSendAudio = false;
  let canSendVideo = false;
  let sendTransport = null;
  let receiveTransport = null;
  return function Mediasoup2(_ref55) {
    let {
      roomId,
      shouldSendAudio,
      shouldReceive,
      shouldSendVideo,
      localAudioStream,
      localVideoStream,
      localScreenStream
    } = _ref55;
    let wsConnected = use(swarm, "connected");
    const {
      hub
    } = swarm;
    shouldSendAudio = shouldSendAudio && !!localAudioStream;
    shouldSendVideo = shouldSendVideo && !!localVideoStream;
    const shouldSendScreen = shouldSendVideo && !!localScreenStream;
    let shouldSend = shouldSendAudio || shouldSendVideo || shouldSendScreen;
    let [isMediasoupInfo, infoPayload] = useEvent(serverEvent, "mediasoup-info");
    if (isMediasoupInfo) {
      canUseMediasoup = true;
      routerRtpCapabilities = infoPayload.rtpCapabilities;
    }
    switch (mediasoupState) {
      case INITIAL2:
        if (canUseMediasoup && (shouldReceive || shouldSend) && wsConnected) {
          connectedRoomId = roomId;
          mediasoupState = LOADING;
          (async () => {
            if (!mediasoupDevice.loaded) {
              await mediasoupDevice.load({
                routerRtpCapabilities
              });
            }
            canSendAudio = mediasoupDevice.canProduce("audio");
            canSendVideo = mediasoupDevice.canProduce("video");
            if (!canSendAudio) console.warn("Mediasoup: cannot send audio");
            if (!canSendVideo) console.warn("Mediasoup: cannot send video");
            let canSend = canSendAudio || canSendVideo;
            mediasoupState = READY;
            if (shouldReceive) initializeReceiving(hub);
            if (shouldSend && canSend) initializeSending(hub);
          })();
        }
        break;
      case READY:
        if (!wsConnected || roomId !== connectedRoomId) {
          log("mediasoup: disconnecting");
          connectedRoomId = null;
          mediasoupState = INITIAL2;
          sendState = INITIAL2;
          sendingAudioStream = null;
          sendingVideoStream = null;
          sendTransport == null ? void 0 : sendTransport.close();
          sendTransport = null;
          stopReceiving();
          if (wsConnected) update8();
          break;
        }
        switch (sendState) {
          case INITIAL2:
            if (shouldSend && wsConnected) initializeSending(hub);
            break;
          case READY:
            if (shouldSendAudio && sendingAudioStream !== localAudioStream) {
              log("mediasoup: sending audio stream");
              sendingAudioStream = localAudioStream;
              sendLocalAudioStream(hub, localAudioStream);
            } else if (!shouldSendAudio && sendingAudioStream) {
              log("mediasoup: removing audio stream");
              sendingAudioStream = null;
              removeLocalAudioStream(hub);
            }
            if (shouldSendVideo && sendingVideoStream !== localVideoStream) {
              log("mediasoup: sending video stream");
              sendingVideoStream = localVideoStream;
              sendLocalVideoStream(hub, localVideoStream);
            } else if (!shouldSendVideo && sendingVideoStream) {
              log("mediasoup: removing video stream");
              sendingVideoStream = null;
              removeLocalVideoStream(hub);
            }
            if (shouldSendScreen && sendingScreenStream !== localScreenStream) {
              log("mediasoup: sending screen stream");
              sendingScreenStream = localScreenStream;
              sendLocalScreenStream(hub, localScreenStream);
            } else if (!shouldSendScreen) {
              log("mediasoup: removing screen stream");
              sendingScreenStream = null;
              removeLocalScreenStream(hub);
            }
        }
        switch (receiveState) {
          case INITIAL2:
            if (shouldReceive && wsConnected) initializeReceiving(hub);
            break;
          case READY:
            if (!shouldReceive) {
              stopReceiving();
            }
        }
        break;
    }
    let [isProducerClosed, producerClosedPayload, acceptProducerClosed] = useEvent(serverEvent, "producer-closed");
    if (isProducerClosed) {
      const {
        peerId,
        streamName
      } = producerClosedPayload;
      remoteStreams = remoteStreams.filter(s3 => !(s3.peerId === peerId.split(".")[0] && s3.name === streamName));
      acceptProducerClosed();
      update8();
    }
    let [isConsumer, consumerPayload, accept] = useEvent(serverEvent, "new-consumer");
    if (isConsumer) {
      let {
        peerId,
        producerId,
        id,
        kind,
        rtpParameters,
        appData
      } = consumerPayload;
      [peerId] = peerId.split(".");
      receiveTransport == null ? void 0 : receiveTransport.consume({
        id,
        producerId,
        kind,
        rtpParameters
      }).then(consumer => {
        accept();
        let track = consumer.track;
        if (!track) return;
        let newStream = new MediaStream([track]);
        let newRemoteStreams = [...remoteStreams];
        let trackType = (appData == null ? void 0 : appData.source) === "screen" ? "screen" : track.kind;
        let i = newRemoteStreams.findIndex(s3 => s3.peerId === peerId && s3.name === trackType);
        if (i === -1) i = newRemoteStreams.length;
        newRemoteStreams[i] = {
          stream: newStream,
          name: trackType,
          peerId
        };
        remoteStreams = newRemoteStreams;
        update8();
      }).catch(console.error);
    }
    return remoteStreams;
  };
  async function initializeSending(hub) {
    sendState = LOADING;
    const {
      id,
      iceParameters,
      iceCandidates,
      dtlsParameters
    } = await hub.sendRequest("mediasoup", {
      type: "createWebRtcTransport",
      data: {
        producing: true,
        consuming: false,
        rtpCapabilities: mediasoupDevice.rtpCapabilities
      }
    });
    sendTransport = mediasoupDevice.createSendTransport({
      id,
      iceParameters,
      iceCandidates,
      dtlsParameters,
      iceServers: []
    });
    sendState = READY;
    sendTransport.on("connect", (_ref56, callback, errback) => {
      let {
        dtlsParameters: dtlsParameters2
      } = _ref56;
      hub.sendRequest("mediasoup", {
        type: "connectWebRtcTransport",
        data: {
          transportId: sendTransport.id,
          dtlsParameters: dtlsParameters2
        }
      }).then(callback).catch(errback);
    });
    sendTransport.on("produce", async (_ref57, callback, errback) => {
      let {
        kind,
        rtpParameters,
        appData
      } = _ref57;
      try {
        const {
          id: id2
        } = await hub.sendRequest("mediasoup", {
          type: "produce",
          data: {
            transportId: sendTransport.id,
            kind,
            rtpParameters,
            appData
          }
        });
        callback({
          id: id2
        });
      } catch (error) {
        errback(error);
      }
    });
    update8();
  }
  async function initializeReceiving(hub) {
    receiveState = LOADING;
    const {
      id,
      iceParameters,
      iceCandidates,
      dtlsParameters,
      sctpParameters
    } = await hub.sendRequest("mediasoup", {
      type: "createWebRtcTransport",
      data: {
        producing: false,
        consuming: true,
        rtpCapabilities: mediasoupDevice.rtpCapabilities
      }
    });
    receiveTransport = mediasoupDevice.createRecvTransport({
      id,
      iceParameters,
      iceCandidates,
      dtlsParameters,
      sctpParameters,
      iceServers: []
    });
    receiveState = READY;
    receiveTransport.on("connect", (_ref58, callback, errback) => {
      let {
        dtlsParameters: dtlsParameters2
      } = _ref58;
      hub.sendRequest("mediasoup", {
        type: "connectWebRtcTransport",
        data: {
          transportId: receiveTransport.id,
          dtlsParameters: dtlsParameters2
        }
      }).then(callback).catch(errback);
    });
    update8();
  }
  function stopReceiving() {
    receiveState = INITIAL2;
    remoteStreams = [];
    receiveTransport == null ? void 0 : receiveTransport.close();
    receiveTransport = null;
  }
  async function sendLocalAudioStream(hub, localStream) {
    const track = localStream.getAudioTracks()[0];
    audioProducer = await sendTransport.produce({
      track,
      codecOptions: {
        opusStereo: 1,
        opusDtx: 1
      },
      appData: {
        source: "main"
      }
    });
    audioProducer.on("transportclose", () => {
      audioProducer = null;
    });
  }
  async function sendLocalVideoStream(hub, localStream) {
    const track = localStream.getVideoTracks()[0];
    videoProducer = await sendTransport.produce({
      track,
      codecOptions: {
        opusStereo: 1,
        opusDtx: 1
      },
      appData: {
        source: "main"
      }
    });
    videoProducer.on("transportclose", () => {
      videoProducer = null;
    });
  }
  async function sendLocalScreenStream(hub, localStream) {
    const track = localStream.getVideoTracks()[0];
    screenProducer = await sendTransport.produce({
      track,
      codecOptions: {
        opusStereo: 1,
        opusDtx: 1
      },
      appData: {
        source: "screen"
      }
    });
    screenProducer.on("transportclose", () => {
      screenProducer = null;
    });
  }
  function removeLocalAudioStream(hub) {
    let producerId = audioProducer == null ? void 0 : audioProducer.id;
    audioProducer == null ? void 0 : audioProducer.close();
    audioProducer = null;
    hub.sendRequest("mediasoup", {
      type: "closeProducer",
      data: {
        producerId
      }
    });
  }
  function removeLocalVideoStream(hub) {
    if (videoProducer) {
      let producerId = videoProducer.id;
      videoProducer.close();
      videoProducer = null;
      hub.sendRequest("mediasoup", {
        type: "closeProducer",
        data: {
          producerId
        }
      });
    }
  }
  function removeLocalScreenStream(hub) {
    if (screenProducer) {
      let producerId = screenProducer.id;
      screenProducer.close();
      screenProducer = null;
      hub.sendRequest("mediasoup", {
        type: "closeProducer",
        data: {
          producerId
        }
      });
    }
  }
}

// ../../jam-core/connections/P2pMedia.js
function P2pMedia(_ref59) {
  let {
    swarm
  } = _ref59;
  let sendingAudioStream = null;
  let sendingVideoStream = null;
  let sendingScreenStream = null;
  return function P2pMedia2(_ref60) {
    let {
      localVideoStream,
      localAudioStream,
      localScreenStream,
      iAmSpeaker,
      iAmPresenter
    } = _ref60;
    let shouldSendAudio = localAudioStream && iAmSpeaker;
    let shouldSendVideo = localVideoStream && iAmPresenter;
    let shouldSendScreen = localScreenStream && iAmPresenter;
    let remoteStreams = use(swarm, "remoteStreams");
    if (shouldSendAudio && sendingAudioStream !== localAudioStream) {
      sendingAudioStream = localAudioStream;
      addLocalStream(swarm, localAudioStream, "audio");
    } else if (!shouldSendAudio && sendingAudioStream) {
      sendingAudioStream = null;
      addLocalStream(swarm, null, "audio");
    }
    if (shouldSendVideo && sendingVideoStream !== localVideoStream) {
      sendingVideoStream = localVideoStream;
      addLocalStream(swarm, localVideoStream, "video");
    } else if (!shouldSendVideo && sendingVideoStream) {
      sendingVideoStream = null;
      addLocalStream(swarm, null, "video");
    }
    if (shouldSendScreen && sendingScreenStream !== localScreenStream) {
      sendingScreenStream = localScreenStream;
      addLocalStream(swarm, localScreenStream, "screen");
    } else if (!shouldSendScreen) {
      sendingScreenStream = null;
      addLocalStream(swarm, null, "screen");
    }
    return remoteStreams;
  };
}

// ../../jam-core/connections/WebRtcConnections.js
function WebRtcConnections(_ref61) {
  let {
    swarm,
    hasMediasoup
  } = _ref61;
  const webRtcConnections = new Set();
  return function WebRtcConnections2(_ref62) {
    let {
      roomState
    } = _ref62;
    let {
      iAmSpeaker,
      room: {
        speakers,
        stageOnly
      }
    } = roomState;
    let peers = use(swarm, "peers");
    const allConnections = new Set();
    for (let peer of Object.values(peers)) {
      for (let connection of Object.values(peer.connections)) {
        allConnections.add(connection);
      }
    }
    for (let connection of webRtcConnections) {
      if (!allConnections.has(connection)) {
        webRtcConnections.delete(connection);
      }
    }
    for (let connection of allConnections) {
      let theyAreSpeaker = stageOnly || speakers.includes(connection.peerId);
      let shouldConnect = hasMediasoup ? iAmSpeaker && theyAreSpeaker : iAmSpeaker || theyAreSpeaker;
      if (shouldConnect && !webRtcConnections.has(connection)) {
        webRtcConnections.add(connection);
        connectPeer(connection);
      } else if (!shouldConnect && webRtcConnections.has(connection)) {
        webRtcConnections.delete(connection);
        disconnectPeer(connection);
      }
    }
  };
}

// ../../jam-core/connections/ConnectMedia.js
function ConnectMedia(_ref63) {
  let {
    roomState,
    swarm,
    hasMediasoup,
    hasBroadcast
  } = _ref63;
  let {
    roomId,
    iAmSpeaker,
    iAmPresenter
  } = roomState;
  let localAudioStream = useRootState("myAudio");
  let localVideoStream = useRootState("myVideo");
  let localScreenStream = useRootState("myScreen");
  let serverRemoteStreams = use(Mediasoup, {
    swarm,
    roomId,
    shouldSendAudio: hasMediasoup && iAmSpeaker,
    shouldSendVideo: hasMediasoup && iAmPresenter,
    shouldReceive: hasMediasoup && !hasBroadcast && !iAmSpeaker,
    localAudioStream,
    localVideoStream,
    localScreenStream
  });
  declare(WebRtcConnections, {
    roomState,
    swarm,
    hasMediasoup
  });
  let p2pRemoteStreams = use(P2pMedia, {
    swarm,
    iAmSpeaker,
    iAmPresenter,
    localAudioStream,
    localVideoStream,
    localScreenStream
  });
  return useStableArray([...p2pRemoteStreams, ...serverRemoteStreams.filter(_ref64 => {
    let {
      peerId
    } = _ref64;
    return !p2pRemoteStreams.find(x => x.peerId === peerId);
  })]);
}

// ../../jam-core/connections/ConnectRoom.js
function ConnectRoom(_ref65) {
  let {
    myIdentity,
    swarm
  } = _ref65;
  let connectedRoomId = null;
  configSwarmIdentity(swarm, myIdentity);
  configSwarm(swarm, staticConfig);
  useOn(staticConfig, () => configSwarm(swarm, staticConfig));
  const identities2 = {};
  let update8 = useUpdate();
  return function ConnectRoom2(_ref66) {
    let {
      roomId,
      hasRoom,
      inRoom,
      myIdentity: myIdentity2,
      roomState
    } = _ref66;
    let myId = myIdentity2.publicKey;
    let shouldConnect = hasRoom && roomId && roomState.iAmAuthorized;
    if (shouldConnect) {
      if (swarm.connectState === INITIAL || swarm.connectState === DISCONNECTED || roomId !== swarm.room || myId !== swarm.myPeerId) {
        connectedRoomId = roomId;
        log("connecting room", roomId);
        swarm.config({
          sign: data2 => signData2(myIdentity2, data2)
        });
        swarm.connect(roomId, myId);
      }
    } else {
      if (swarm.connectState !== INITIAL) {
        connectedRoomId = null;
        swarm.disconnect();
      }
    }
    let {
      peerEvent,
      serverEvent
    } = swarm;
    let [isNewPeer, id] = useEvent(swarm, "newPeer");
    if (isNewPeer) {
      (async () => {
        for (let i = 0; i < 5; i++) {
          let [data2, ok] = await get(`/identities/${id}`);
          if (ok) {
            identities2[id] = data2;
            update8();
            return;
          } else {
            await new Promise(r => setTimeout(r, 1e3));
          }
        }
      })();
    }
    let [isIdentityUpdate, peerId, data] = useEvent(peerEvent, "identity-update");
    if (isIdentityUpdate) {
      identities2[peerId] = data;
    }
    let [isRoomUpdate, room] = useEvent(serverEvent, "room-info");
    if (isRoomUpdate) {
      log("new room info", room);
      if (connectedRoomId !== null) {
        populateApiCache(`/rooms/${connectedRoomId}`, room);
      }
    }
    return {
      identities: useStableObject({
        ...identities2
      }),
      otherDeviceInRoom: declare(OtherDeviceInRoom, {
        swarm,
        inRoom,
        myId
      })
    };
  };
}
function OtherDeviceInRoom(_ref67) {
  let {
    swarm,
    inRoom,
    myId
  } = _ref67;
  let [otherDeviceInRoom, setState] = useState(false);
  let dispatch = useDispatch();
  let [isMyConnection, myConnState] = useEvent(swarm.connectionState, myId);
  if (isMyConnection) {
    if (myConnState === void 0) {
      otherDeviceInRoom = false;
    } else {
      let {
        states,
        latest
      } = myConnState;
      let {
        myConnId
      } = swarm;
      otherDeviceInRoom = false;
      for (let connId in states) {
        if (connId !== myConnId && states[connId].state.inRoom) {
          otherDeviceInRoom = true;
          if (connId === latest && inRoom) {
            dispatch(actions.JOIN, null);
          }
          break;
        }
      }
    }
    setState(otherDeviceInRoom);
  }
  return otherDeviceInRoom;
}
function configSwarmIdentity(swarm, myIdentity) {
  let myId = myIdentity.publicKey;
  swarm.config({
    myPeerId: myId,
    sign: data => signData2(myIdentity, data),
    verify: verifyData2
  });
}
function configSwarm(swarm, staticConfig2) {
  swarm.config({
    url: staticConfig2.urls.pantry,
    autoConnect: false,
    debug: staticConfig2.development,
    reduceState: (_states, _current, latest, findLatest2) => {
      var _a4;
      if (latest.inRoom) return latest;
      return (_a4 = findLatest2(s3 => s3.inRoom)) != null ? _a4 : latest;
    },
    pcConfig: {
      iceTransportPolicy: "all",
      iceServers: [{
        urls: [`${staticConfig2.urls.stun}`, `stun:coturn.jam.systems:3478`]
      }, {
        ...staticConfig2.urls.turnCredentials,
        urls: `${staticConfig2.urls.turn}`
      }]
    }
  });
}

// ../../jam-core/serverRecording.js
import { is as is10 } from "minimal-state";
function ServerRecording(_ref68) {
  let {
    swarm
  } = _ref68;
  let isServerRecording = false;
  return function ServerRecording2(_ref69) {
    let {} = _ref69;
    const {
      hub
    } = swarm;
    const [start] = useAction(actions.START_SERVER_RECORDING);
    const [stop] = useAction(actions.STOP_SERVER_RECORDING);
    console.log(start, stop);
    if (start) {
      hub.sendRequest("mediasoup", {
        type: "startServerRecording"
      });
      isServerRecording = true;
    }
    if (stop) {
      hub.sendRequest("mediasoup", {
        type: "stopServerRecording"
      });
      isServerRecording = false;
    }
    return {
      isServerRecording
    };
  };
}

// ../../jam-core/connections/BroadcastReceiver.js
import shaka from "shaka-player";
function BroadcastReceiver(_ref70) {
  let {
    swarm
  } = _ref70;
  let update8 = useUpdate();
  let peerPlayers = new Map();
  let updating = false;
  return function BroadcastReceiver2(_ref71) {
    let {
      roomId,
      shouldReceive,
      roomState,
      inRoom
    } = _ref71;
    const peerState = use(swarm, "peerState");
    if (!inRoom || !shouldReceive) {
      for (const player of peerPlayers.values()) {
        const element = player.getMediaElement();
        player.destroy();
        element.remove();
      }
      peerPlayers.clear();
      return peerPlayers;
    }
    if (!updating) {
      const stateDiff = needsUpdate(peerPlayers, peerState, roomState);
      if (stateDiff.toRemove.length > 0 || stateDiff.toAdd.length > 0) {
        updating = true;
        updatePeerPlayers({
          roomId,
          stateDiff
        }).then(update8);
      }
    }
    return peerPlayers;
  };
  async function updatePeerPlayers(_ref72) {
    let {
      roomId,
      stateDiff
    } = _ref72;
    console.log(`Adding ${stateDiff.toAdd.length} players and removing ${stateDiff.toRemove.length}`, JSON.stringify(stateDiff));
    const newPeerPlayers = new Map();
    for (const playerIdToRemove of stateDiff.toRemove) {
      const player = peerPlayers.get(playerIdToRemove);
      const element = player.getMediaElement();
      player.destroy();
      element.remove();
      peerPlayers.delete(playerIdToRemove);
    }
    for (const playerId of stateDiff.expectedPlayerIds) {
      const [source, peerId] = playerId.split("|");
      console.log(peerPlayers.has(playerId));
      if (peerPlayers.has(playerId)) {
        newPeerPlayers.set(playerId, peerPlayers.get(playerId));
      } else {
        newPeerPlayers.set(playerId, await createPeerPlayer({
          roomId,
          peerId,
          source
        }));
      }
    }
    peerPlayers = newPeerPlayers;
    updating = false;
  }
  async function createPeerPlayer(_ref73) {
    let {
      roomId,
      peerId,
      source
    } = _ref73;
    const mediaElement = document.createElement("video");
    const broadcastUrl = `${staticConfig.urls.pantry}/stream/hls/${roomId}/${peerId}/${source}/index.m3u8`;
    const player = new shaka.Player(mediaElement);
    await player.load(broadcastUrl);
    return player;
  }
  function needsUpdate(remoteStreams, peerState, roomState) {
    var _a4;
    const expectedPlayerIds = new Set();
    for (const speakerPeerId of ((_a4 = roomState == null ? void 0 : roomState.room) == null ? void 0 : _a4.speakers) || []) {
      const peer = peerState[speakerPeerId];
      if (peer) {
        if (!peer.micMuted) {
          expectedPlayerIds.add(`main|${speakerPeerId}`);
        }
        if (roomState.room.presenters.includes(speakerPeerId) && speakerPeerId && !!peer.camOn) {
          expectedPlayerIds.add(`main|${speakerPeerId}`);
        }
        if (roomState.room.presenters.includes(speakerPeerId) && speakerPeerId && !!peer.shareScreen) {
          expectedPlayerIds.add(`screen|${speakerPeerId}`);
        }
      }
    }
    const currentPlayerIds = new Set([...peerPlayers.keys()]);
    const toAdd = [...expectedPlayerIds].filter(x => !currentPlayerIds.has(x));
    const toRemove = [...currentPlayerIds].filter(x => !expectedPlayerIds.has(x));
    return {
      toRemove,
      toAdd,
      expectedPlayerIds
    };
  }
}

// ../../jam-core/AppState.js
function AppState(_ref74) {
  let {
    hasMediasoup,
    hasBroadcast
  } = _ref74;
  const swarm = Swarm();
  const {
    peerState,
    myPeerState
  } = swarm;
  is11(myPeerState, {
    inRoom: false,
    micMuted: false,
    leftStage: false,
    isRecording: false,
    camOn: false,
    shareScreen: false
  });
  return function AppState2(_ref75) {
    let {
      roomId,
      userInteracted,
      micMuted,
      handRaised,
      autoJoin,
      autoRejoin,
      customStream
    } = _ref75;
    let myIdentity = use(Identity, {
      swarm,
      roomId
    });
    if (!myIdentity) return {
      swarm
    };
    let myId = myIdentity.publicKey;
    let roomState = use(RoomState, {
      roomId,
      myIdentity,
      peerState,
      myPeerState
    });
    let {
      room,
      iAmSpeaker,
      iAmModerator,
      iAmPresenter,
      hasRoom
    } = roomState;
    let inRoom = use(InRoom, {
      roomState,
      autoJoin,
      autoRejoin
    });
    declare(ModeratorState, {
      swarm,
      moderators: room.moderators,
      handRaised
    });
    let remoteStreams = use(ConnectMedia, {
      roomState,
      hasMediasoup,
      hasBroadcast,
      swarm
    });
    let broadcastPlayers = use(BroadcastReceiver, {
      swarm,
      roomId,
      roomState,
      peerState,
      inRoom,
      shouldReceive: userInteracted && hasBroadcast && !iAmSpeaker
    });
    is11(myPeerState, {
      micMuted,
      inRoom: !!inRoom
    });
    declare(Reactions, {
      swarm
    });
    return merge({
      swarm,
      micMuted,
      handRaised,
      inRoom,
      myId,
      myIdentity,
      remoteStreams
    }, roomState, declare(PeerState, {
      swarm
    }), declare(ConnectRoom, {
      roomId,
      hasRoom,
      inRoom,
      swarm,
      myIdentity,
      roomState
    }), declare(AudioState, {
      myId,
      inRoom,
      iAmSpeaker,
      iAmModerator,
      moderators: room.moderators,
      swarm,
      remoteStreams,
      broadcastPlayers,
      userInteracted,
      micMuted,
      handRaised,
      customStream
    }), declare(VideoState, {
      inRoom,
      iAmPresenter,
      remoteStreams
    }), declare(BackChannel, {
      swarm,
      myId
    }), declare(ServerRecording, {
      swarm
    }));
  };
}
function PeerState(_ref76) {
  let {
    swarm
  } = _ref76;
  var _a4;
  let peers = useStableArray(Object.keys((_a4 = use(swarm, "peers")) != null ? _a4 : {}));
  let peerState = useStableObject({
    ...use(swarm, "peerState")
  });
  let myPeerState = useStableObject({
    ...use(swarm, "myPeerState")
  });
  return {
    peers,
    peerState,
    myPeerState
  };
}
function InRoom() {
  let inRoom = null;
  let autoJoinCount = 0;
  let didAutoJoin = false;
  const joinedRooms = StoredState("jam.joinedRooms", () => ({}));
  return function InRoom2(_ref77) {
    let {
      roomState,
      autoJoin,
      autoRejoin
    } = _ref77;
    let {
      roomId,
      hasRoom,
      room: {
        closed
      },
      iAmModerator
    } = roomState;
    let [isJoinRoom, joinedRoomId] = useAction(actions.JOIN);
    let [isAutoJoin] = useAction(actions.AUTO_JOIN);
    if ((isAutoJoin || autoJoin && !didAutoJoin) && autoJoinCount === 0) {
      didAutoJoin = true;
      autoJoinCount = 1;
    }
    if (!roomId || closed && !iAmModerator) {
      inRoom = null;
    } else {
      if (isJoinRoom) {
        inRoom = joinedRoomId;
      } else if (autoRejoin && hasRoom && joinedRooms[roomId]) {
        inRoom = roomId;
      }
      if (autoJoinCount > 0 && hasRoom) {
        autoJoinCount--;
        inRoom = roomId;
      }
    }
    if (autoRejoin) is11(joinedRooms, roomId, inRoom !== null || void 0);
    return inRoom;
  };
}

// ../../jam-core.ts
function createApi(state, dispatch, setProps2) {
  return {
    setProps: setProps2,
    setState: (keyOrValue, value) => {
      is12(state, keyOrValue, value);
    },
    onState: (keyOrListener, listenerOrNone) => {
      return on8(state, keyOrListener, listenerOrNone);
    },
    createRoom: (roomId, partialRoom) => createRoom(state, roomId, partialRoom),
    updateRoom: (roomId, room) => updateRoom(state, roomId, room),
    getRoom: roomId => getRoom(roomId),
    getDisplayName: (info, room) => displayName(info, room),
    addSpeaker: (roomId, peerId) => addSpeaker(state, roomId, peerId),
    addModerator: (roomId, peerId) => addModerator(state, roomId, peerId),
    addPresenter: (roomId, peerId) => addPresenter(state, roomId, peerId),
    removeSpeaker: (roomId, peerId) => removeSpeaker(state, roomId, peerId),
    removeModerator: (roomId, peerId) => removeModerator(state, roomId, peerId),
    removePresenter: (roomId, peerId) => removePresenter(state, roomId, peerId),
    addAdmin: peerId => addAdmin(state, peerId),
    removeAdmin: peerId => removeAdmin(state, peerId),
    updateInfo: info => updateInfo(state, info),
    enterRoom: roomId => dispatch(actions.JOIN, roomId),
    leaveRoom: () => dispatch(actions.JOIN, null),
    leaveStage: () => dispatch(actions.LEAVE_STAGE),
    sendReaction: reaction => dispatch(actions.REACTION, reaction),
    retryMic: () => dispatch(actions.RETRY_MIC),
    retryAudio: () => dispatch(actions.RETRY_AUDIO),
    autoJoinOnce: () => dispatch(actions.AUTO_JOIN),
    switchCamera: () => dispatch(actions.SWITCH_CAM),
    setCameraOn: cameraOn => dispatch(actions.SET_CAM_ON, cameraOn),
    selectMicrophone: mic => dispatch(actions.SELECT_MIC, mic),
    startScreenShare: () => dispatch(actions.START_SCREEN_SHARE),
    stopScreenShare: () => dispatch(actions.STOP_SCREEN_SHARE),
    startRecording: () => dispatch("start-recording"),
    stopRecording: () => dispatch("stop-recording"),
    getRecordingsDownloadLink: roomId => recordingsDownloadLink(state, roomId),
    startServerRecording: () => dispatch(actions.START_SERVER_RECORDING),
    stopServerRecording: () => dispatch(actions.STOP_SERVER_RECORDING),
    downloadRecording: fileName => dispatch("download-recording", fileName),
    startPodcastRecording: () => dispatch("start-podcast-recording"),
    stopPodcastRecording: () => dispatch("stop-podcast-recording"),
    backchannelSubscribe: (roomId, topic, handler, subscriptionId) => dispatch(actions.BACKCHANNEL_SUBSCRIBE, {
      roomId,
      topic,
      handler,
      subscriptionId
    }),
    backchannelUnsubscribe: subscriptionId => dispatch(actions.BACKCHANNEL_UNSUBSCRIBE, subscriptionId),
    backchannelBroadcast: (roomId, topic, data) => dispatch(actions.BACKCHANNEL_BROADCAST, {
      roomId,
      topic,
      data
    })
  };
}
function createJam(_temp2) {
  let {
    jamConfig,
    initialProps,
    cachedRooms,
    debug: debug_ = false
  } = _temp2 === void 0 ? {} : _temp2;
  var _a4, _b, _c, _d;
  if (jamConfig) {
    let {
      domain
    } = jamConfig;
    if (domain) {
      let jamConfigUrls = (_a4 = jamConfig.urls) != null ? _a4 : {
        turnCredentials: staticConfig.urls.turnCredentials
      };
      jamConfigUrls.pantry = (_b = jamConfigUrls.pantry) != null ? _b : `https://${domain}/_/pantry`;
      jamConfigUrls.stun = (_c = jamConfigUrls.stun) != null ? _c : `stun:${domain}:3478`;
      jamConfigUrls.turn = (_d = jamConfigUrls.turn) != null ? _d : `turn:${domain}:3478`;
      jamConfig.urls = jamConfigUrls;
      delete jamConfig.domain;
    }
    set6(staticConfig, jamConfig);
  }
  if (cachedRooms) {
    for (let roomId in cachedRooms) {
      populateApiCache(`/rooms/${roomId}`, cachedRooms[roomId]);
    }
  }
  if (debug_ || (jamConfig == null ? void 0 : jamConfig.development)) {
    if (debug_) window.DEBUG = true;
    debugStateTree();
  }
  let props = {
    ...defaultProps,
    ...initialProps,
    hasMediasoup: !!staticConfig.sfu,
    hasBroadcast: !!staticConfig.broadcast
  };
  const {
    state,
    dispatch,
    setProps: setProps2
  } = declareStateRoot(AppState, props, {
    state: void 0,
    defaultState
  });
  const api = createApi(state, dispatch, setProps2);
  if (debug_ || (jamConfig == null ? void 0 : jamConfig.development)) {
    if (debug_) debug(state.swarm);
    window.swarm = state.swarm;
    window.state = state;
    window.api = api;
    debug(state);
  }
  return [state, api];
}
export { apiUrl, createApi, createJam, defaultState, importDefaultIdentity, importRoomIdentity, is12 as is, on8 as on, set6 as set, until2 as until, update7 as update };
/*! simple-peer. MIT License. Feross Aboukhadijeh <https://feross.org/opensource> */